// CHEF-APP - Diese Datei gehört in: chef-app/src/App.tsx
import React, { useState, useEffect, } from 'react';
import PinLogin from './PinLogin';

// =================== FIREBASE INTEGRATION ===================
import { initializeApp } from 'firebase/app';
import { getDatabase, ref, set, onValue, runTransaction, update, get, push } from 'firebase/database';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyCaUBolpNmTNucUG14nN7PKFijYL05ZpoY",
  authDomain: "werkstatt-app-561f9.firebaseapp.com",
  databaseURL: "https://werkstatt-app-561f9-default-rtdb.europe-west1.firebasedatabase.app",
  projectId: "werkstatt-app-561f9",
  storageBucket: "werkstatt-app-561f9.firebasestorage.app",
  messagingSenderId: "985738369659",
  appId: "1:985738369659:web:20c8ef37f6313092aba0de"
};

// Firebase initialisieren
const app = initializeApp(firebaseConfig);
const database = getDatabase(app);
const firestore = getFirestore(app);
const storage = getStorage(app);

// =================== ÖSTERREICHISCHE FEIERTAGE ===================
interface Holiday {
  date: string;
  name: string;
}

function getAustrianHolidays(year: number): Holiday[] {
  const holidays: Holiday[] = [];
  
  // Feste Feiertage
  holidays.push({ date: `${year}-01-01`, name: "Neujahr" });
  holidays.push({ date: `${year}-01-06`, name: "Heilige Drei Könige" });
  holidays.push({ date: `${year}-05-01`, name: "Staatsfeiertag" });
  holidays.push({ date: `${year}-08-15`, name: "Mariä Himmelfahrt" });
  holidays.push({ date: `${year}-10-26`, name: "Nationalfeiertag" });
  holidays.push({ date: `${year}-11-01`, name: "Allerheiligen" });
  holidays.push({ date: `${year}-12-08`, name: "Mariä Empfängnis" });
  holidays.push({ date: `${year}-12-25`, name: "Christtag" });
  holidays.push({ date: `${year}-12-26`, name: "Stefanitag" });
  
  // Bewegliche Feiertage (Ostern-abhängig)
  const easter = calculateEaster(year);
  
  // Karfreitag (2 Tage vor Ostern) - nur für evangelische Arbeitnehmer, aber oft arbeitsfrei
  holidays.push({ 
    date: addDaysToDate(easter, -2), 
    name: "Karfreitag" 
  });
  
  // Ostermontag (1 Tag nach Ostern)
  holidays.push({ 
    date: addDaysToDate(easter, 1), 
    name: "Ostermontag" 
  });
  
  // Christi Himmelfahrt (39 Tage nach Ostern)
  holidays.push({ 
    date: addDaysToDate(easter, 39), 
    name: "Christi Himmelfahrt" 
  });
  
  // Pfingstmontag (50 Tage nach Ostern)
  holidays.push({ 
    date: addDaysToDate(easter, 50), 
    name: "Pfingstmontag" 
  });
  
  // Fronleichnam (60 Tage nach Ostern)
  holidays.push({ 
    date: addDaysToDate(easter, 60), 
    name: "Fronleichnam" 
  });
  
  return holidays;
}

// Berechnung des Osterdatums (Gaußsche Osterformel)
function calculateEaster(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  
  return new Date(year, month - 1, day);
}

// Hilfsfunktion: Tage zu Datum hinzufügen
function addDaysToDate(date: Date, days: number): string {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result.toISOString().split('T')[0];
}

// Deep Comparison Helper
const deepEqual = (obj1: any, obj2: any): boolean => {
  if (obj1 === obj2) return true;
  if (!obj1 || !obj2) return false;
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object') return obj1 === obj2;
  
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  
  if (keys1.length !== keys2.length) return false;
  
  for (const key of keys1) {
    if (!keys2.includes(key)) return false;
    if (!deepEqual(obj1[key], obj2[key])) return false;
  }
  
  return true;
};

// Bildkomprimierung
async function compressImage(file: File, maxDim = 1200, quality = 0.8): Promise<File> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;
        
        let width = img.width;
        let height = img.height;
        
        if (width > height && width > maxDim) {
          height *= maxDim / width;
          width = maxDim;
        } else if (height > maxDim) {
          width *= maxDim / height;
          height = maxDim;
        }
        
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob((blob) => {
          resolve(new File([blob!], file.name, {
            type: 'image/jpeg',
            lastModified: Date.now()
          }));
        }, 'image/jpeg', quality);
      };
    };
  });
}

// Chef Authentifizierung
async function authenticateChef(name: string, pin: string): Promise<boolean> {
  try {
    const chefDoc = await getDoc(doc(firestore, 'users', 'chef'));
    if (chefDoc.exists()) {
      const data = chefDoc.data();
      return data.pin === pin;
    }
    return false;
  } catch (error) {
    console.error('Auth Fehler:', error);
    return false;
  }
}

// Firebase Upload Funktion
async function uploadPhotoToFirebase(file: File, orderId: string): Promise<string> {
  const timestamp = Date.now();
  const fileName = `orders/${orderId}/photo_${timestamp}.jpg`;
  const photoRef = storageRef(storage, fileName);
  
  const snapshot = await uploadBytes(photoRef, file);
  const downloadURL = await getDownloadURL(snapshot.ref);
  
  return downloadURL;
}

// =================== SECURITY UTILS ===================

// KORRIGIERTER Storage Manager mit KRITISCHEN FIXES
const StorageManager = {
  KEYS: {
    ORDERS: 'orders/data',
    DAILY_HOURS: 'dailyHours/entries',
    MESSAGES: 'messages/list',
    METADATA: 'orders/metadata',
    BACKUPS: 'backups'
  },

  // KRITISCH: Backup vor JEDER Änderung
  createBackup: async (orderId: string, data: any, action: string): Promise<void> => {
    try {
      const backupRef = push(ref(database, `${StorageManager.KEYS.BACKUPS}/${orderId}`));
      await set(backupRef, {
        timestamp: new Date().toISOString(),
        action,
        data: JSON.parse(JSON.stringify(data)), // Deep copy
        user: sessionStorage.getItem('user') || 'unknown'
      });
      
      // Behalte nur die letzten 50 Backups pro Auftrag
      const backupsRef = ref(database, `${StorageManager.KEYS.BACKUPS}/${orderId}`);
      const snapshot = await get(backupsRef);
      if (snapshot.exists()) {
        const backups = Object.entries(snapshot.val())
          .sort(([, a]: any, [, b]: any) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
        
        if (backups.length > 50) {
          // Lösche alte Backups
          for (let i = 50; i < backups.length; i++) {
            await set(ref(database, `${StorageManager.KEYS.BACKUPS}/${orderId}/${backups[i][0]}`), null);
          }
        }
      }
    } catch (error) {
      console.error('Backup-Fehler:', error);
    }
  },

  // KRITISCH KORRIGIERT: Sichere Speicherung mit Validierung
  saveOrder: async (orderId: string, updates: any, currentUser: string): Promise<boolean> => {
    try {
      const orderRef = ref(database, `${StorageManager.KEYS.ORDERS}/${orderId}`);
      
      // WICHTIG: Hole ZUERST die aktuellen Daten für Backup
      const currentSnapshot = await get(orderRef);
      if (currentSnapshot.exists()) {
        await StorageManager.createBackup(orderId, currentSnapshot.val(), 'pre-update');
      }
      
      let success = false;
      let conflictDetected = false;
      
      await runTransaction(orderRef, (currentData) => {
        // Neuer Auftrag
        if (!currentData) {
          success = true;
          return {
            ...updates,
            id: orderId,
            createdAt: new Date().toISOString(),
            version: 1,
            lastModifiedBy: currentUser,
            changeHistory: [{
              id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              timestamp: new Date().toISOString(),
              user: currentUser,
              action: 'Erstellt',
              changes: updates
            }]
          };
        }
        
        // KRITISCH: Version Check
        if (updates._expectedVersion && currentData.version !== updates._expectedVersion) {
          conflictDetected = true;
          return; // Abort transaction
        }
        
        // Erstelle merged data
        let mergedData = { ...currentData };
        
        // KRITISCH: Arrays IMMER komplett ersetzen, nie mergen!
        if (updates.hasOwnProperty('timeEntries')) {
          // Validiere timeEntries
          if (Array.isArray(updates.timeEntries)) {
            mergedData.timeEntries = updates.timeEntries.map((entry: any) => ({
              ...entry,
              id: entry.id || `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
            }));
          } else {
            console.error('timeEntries ist kein Array!');
            return;
          }
        }
        
        if (updates.hasOwnProperty('materials')) {
          if (Array.isArray(updates.materials)) {
            mergedData.materials = updates.materials;
          }
        }
        
        if (updates.hasOwnProperty('photos')) {
          if (Array.isArray(updates.photos)) {
            mergedData.photos = updates.photos;
          }
        }
        
        // Tasks mit Priority
        if (updates.hasOwnProperty('tasks')) {
          mergedData.tasks = updates.tasks;
        }
        
        if (updates.hasOwnProperty('tasksPriority')) {
          mergedData.tasksPriority = updates.tasksPriority;
        }
        
        // Andere Felder normal updaten
        Object.keys(updates).forEach(key => {
          if (!['timeEntries', 'materials', 'photos', 'tasks', '_expectedVersion'].includes(key)) {
            mergedData[key] = updates[key];
          }
        });
        
        // Metadaten updaten
        mergedData.version = (currentData.version || 0) + 1;
        mergedData.lastModified = new Date().toISOString();
        mergedData.lastModifiedBy = currentUser;
        
        // Change History mit eindeutiger ID
        const changeId = `${Date.now()}_${currentUser}_${Math.random().toString(36).substr(2, 9)}`;
        mergedData.changeHistory = [
          ...(currentData.changeHistory || []),
          {
            id: changeId,
            timestamp: new Date().toISOString(),
            user: currentUser,
            action: determineAction(updates),
            changes: updates,
            previousVersion: currentData.version || 0
          }
        ];
        
        success = true;
        return mergedData;
      });
      
      if (conflictDetected) {
        throw new Error('VERSION_CONFLICT');
      }
      
      if (success) {
        // Update Metadata
        await update(ref(database, StorageManager.KEYS.METADATA), {
          lastUpdate: new Date().toISOString(),
          lastUpdateBy: currentUser
        });
      }
      
      return success;
    } catch (error: any) {
      console.error('Speicherfehler:', error);
      if (error.message === 'VERSION_CONFLICT') {
        throw error;
      }
      return false;
    }
  },

  // Spezielle Methode für Zeiteinträge
  addTimeEntry: async (orderId: string, hours: number, employee: string): Promise<boolean> => {
    try {
      const orderRef = ref(database, `${StorageManager.KEYS.ORDERS}/${orderId}`);
      const snapshot = await get(orderRef);
      
      if (!snapshot.exists()) {
        console.error('Auftrag nicht gefunden');
        return false;
      }
      
      const currentOrder = snapshot.val();
      const newEntry = {
        id: `${Date.now()}_${employee}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date().toISOString(),
        hours: hours,
        employee: employee
      };
      
      const updatedTimeEntries = [...(currentOrder.timeEntries || []), newEntry];
      
      return await StorageManager.saveOrder(
        orderId, 
        { 
          timeEntries: updatedTimeEntries,
          _expectedVersion: currentOrder.version 
        }, 
        employee
      );
    } catch (error) {
      console.error('Fehler beim Hinzufügen der Zeit:', error);
      return false;
    }
  },

  // Sichere Löschung von Zeiteinträgen
  deleteTimeEntry: async (orderId: string, entryId: string, currentUser: string): Promise<boolean> => {
    try {
      const orderRef = ref(database, `${StorageManager.KEYS.ORDERS}/${orderId}`);
      const snapshot = await get(orderRef);
      
      if (!snapshot.exists()) return false;
      
      const currentOrder = snapshot.val();
      const updatedTimeEntries = (currentOrder.timeEntries || []).filter((e: any) => e.id !== entryId);
      
      return await StorageManager.saveOrder(
        orderId,
        {
          timeEntries: updatedTimeEntries,
          _expectedVersion: currentOrder.version
        },
        currentUser
      );
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
      return false;
    }
  },

  // ✅ KRITISCH KORRIGIERT: Verwende Transaction und Merge statt Überschreiben
  saveDailyHours: async (dailyHours: any[]): Promise<boolean> => {
    try {
      const hoursRef = ref(database, StorageManager.KEYS.DAILY_HOURS);
      
      // Verwende Transaction für atomare Updates
      const result = await runTransaction(hoursRef, (currentData) => {
        console.log('Transaction: Aktuelle Daten:', currentData?.length || 0, 'Einträge');
        
        // Wenn keine Daten existieren, initialisiere mit den neuen Daten
        if (!currentData || !Array.isArray(currentData)) {
          console.log('Transaction: Keine existierenden Daten, setze neue Daten');
          return dailyHours;
        }
        
        // Konvertiere zu Map für effizientes Merging
        const currentMap = new Map();
        currentData.forEach(entry => {
          if (entry && entry.employee && entry.date) {
            const key = `${entry.employee}_${entry.date}`;
            currentMap.set(key, entry);
          }
        });
        
        // Merge neue Daten
        let hasChanges = false;
        dailyHours.forEach(newEntry => {
          if (newEntry && newEntry.employee && newEntry.date) {
            const key = `${newEntry.employee}_${newEntry.date}`;
            const existingEntry = currentMap.get(key);
            
            // Nur updaten wenn sich was geändert hat
            if (!existingEntry || existingEntry.hours !== newEntry.hours) {
              currentMap.set(key, {
                ...newEntry,
                updatedAt: new Date().toISOString()
              });
              hasChanges = true;
            }
          }
        });
        
        // Nur speichern wenn Änderungen vorhanden
        if (!hasChanges) {
          console.log('Transaction: Keine Änderungen gefunden, abort');
          return; // Abort transaction
        }
        
        // Konvertiere zurück zu Array
        const mergedData = Array.from(currentMap.values());
        console.log('Transaction: Speichere', mergedData.length, 'Einträge');
        
        return mergedData;
      });
      
      if (result.committed) {
        // Update Metadata
        await update(ref(database, 'dailyHours/metadata'), {
          lastUpdate: new Date().toISOString(),
          updatedBy: sessionStorage.getItem('user') || 'unknown'
        });
        console.log('DailyHours erfolgreich gespeichert');
        return true;
      } else {
        console.log('Transaction abgebrochen - keine Änderungen');
        return true; // Kein Fehler, nur keine Änderungen
      }
    } catch (error) {
      console.error('Fehler beim Speichern der DailyHours:', error);
      return false;
    }
  },

  // ✅ NEU: Einzelner Eintrag Update (EMPFOHLEN!)
  upsertDailyHour: async (employee: string, date: string, hours: number): Promise<boolean> => {
    try {
      const hoursRef = ref(database, StorageManager.KEYS.DAILY_HOURS);
      
      const result = await runTransaction(hoursRef, (currentData) => {
        // Initialisiere mit leerem Array wenn keine Daten
        if (!currentData) currentData = [];
        if (!Array.isArray(currentData)) {
          console.error('DailyHours ist kein Array!');
          return;
        }
        
        // Finde existierenden Eintrag
        const existingIndex = currentData.findIndex((entry: any) => 
          entry && entry.employee === employee && entry.date === date
        );
        
        if (existingIndex >= 0) {
          // Update existing
          console.log(`Update DailyHour: ${employee} - ${date} - ${hours}h`);
          currentData[existingIndex] = {
            ...currentData[existingIndex],
            hours,
            updatedAt: new Date().toISOString()
          };
        } else {
          // Add new
          console.log(`Neue DailyHour: ${employee} - ${date} - ${hours}h`);
          currentData.push({
            id: `${Date.now()}_${employee}_${Math.random().toString(36).substr(2, 9)}`,
            employee,
            date,
            hours,
            createdAt: new Date().toISOString()
          });
        }
        
        return currentData;
      });
      
      if (result.committed) {
        await update(ref(database, 'dailyHours/metadata'), {
          lastUpdate: new Date().toISOString(),
          updatedBy: employee
        });
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Fehler beim Upsert der DailyHour:', error);
      return false;
    }
  },

  saveMessages: async (messages: any[]): Promise<boolean> => {
    try {
      await set(ref(database, StorageManager.KEYS.MESSAGES), messages);
      
      await update(ref(database, 'messages/metadata'), {
        lastUpdate: new Date().toISOString(),
        updatedBy: sessionStorage.getItem('user') || 'unknown',
        count: messages.length
      });
      
      return true;
    } catch (error) {
      console.error('Fehler beim Speichern der Messages:', error);
      return false;
    }
  },

  loadAllOrders: async (): Promise<any[]> => {
    try {
      const ordersRef = ref(database, StorageManager.KEYS.ORDERS);
      const snapshot = await get(ordersRef);
      
      if (snapshot.exists()) {
        const ordersObj = snapshot.val();
        return Object.values(ordersObj);
      }
      return [];
    } catch (error) {
      console.error('Fehler beim Laden der Aufträge:', error);
      return [];
    }
  },

  // Wiederherstellung aus Backup
  restoreFromBackup: async (orderId: string, backupId: string): Promise<boolean> => {
    try {
      const backupRef = ref(database, `${StorageManager.KEYS.BACKUPS}/${orderId}/${backupId}`);
      const snapshot = await get(backupRef);
      
      if (!snapshot.exists()) return false;
      
      const backup = snapshot.val();
      const orderRef = ref(database, `${StorageManager.KEYS.ORDERS}/${orderId}`);
      
      await set(orderRef, {
        ...backup.data,
        restoredAt: new Date().toISOString(),
        restoredFrom: backupId,
        restoredBy: sessionStorage.getItem('user') || 'unknown'
      });
      
      return true;
    } catch (error) {
      console.error('Wiederherstellungsfehler:', error);
      return false;
    }
  }
};

// Bestätigungs-Dialog Hook
const useConfirmDialog = () => {
  const confirm = (message: string, confirmText = 'LÖSCHEN') => {
    return new Promise<boolean>((resolve) => {
      const userInput = window.prompt(
        `${message}\n\nZur Bestätigung bitte "${confirmText}" eingeben:`
      );
      resolve(userInput === confirmText);
    });
  };

  const simpleConfirm = (message: string) => {
    return window.confirm(message);
  };

  return { confirm, simpleConfirm };
};

// Soft-Delete Funktionen
const SoftDelete = {
  markAsDeleted: (order: any, deletedBy: string) => ({
    ...order,
    deleted: true,
    deletedAt: new Date().toISOString(),
    deletedBy: deletedBy,
    previousStatus: order.status,
    status: 'Gelöscht'
  }),

  restore: (order: any) => ({
    ...order,
    deleted: false,
    deletedAt: null,
    deletedBy: null,
    status: order.previousStatus || 'Neu',
    restoredAt: new Date().toISOString()
  }),

  filterActive: (orders: any[]) => orders.filter(o => !o.deleted),
  filterDeleted: (orders: any[]) => orders.filter(o => o.deleted)
};

// Änderungsprotokoll
const determineAction = (changes: any) => {
  if (changes.deleted) return 'Gelöscht';
  if (changes.status) return `Status geändert zu ${changes.status}`;
  if (changes.assignedTo) return 'Zuweisung geändert';
  if (changes.timeEntries) return 'Arbeitszeit aktualisiert';
  if (changes.materials) return 'Material aktualisiert';
  if (changes.photos) return 'Foto hinzugefügt';
  if (changes.tasks) return 'Aufgaben aktualisiert';
  return 'Aktualisiert';
};

const getPreviousValues = (order: any, changes: any) => {
  const previous: any = {};
  Object.keys(changes).forEach(key => {
    if (order[key] !== undefined) {
      previous[key] = order[key];
    }
  });
  return previous;
};

// Session-Timeout Manager
const SessionManager = {
  TIMEOUT_DURATION: 30 * 60 * 1000,
  WARNING_BEFORE: 5 * 60 * 1000,

  init: (onTimeout: () => void, onWarning: () => void) => {
    let timeoutId: NodeJS.Timeout;
    let warningId: NodeJS.Timeout;

    const resetTimer = () => {
      clearTimeout(timeoutId);
      clearTimeout(warningId);

      warningId = setTimeout(() => {
        if (onWarning) {
          onWarning();
        }
      }, SessionManager.TIMEOUT_DURATION - SessionManager.WARNING_BEFORE);

      timeoutId = setTimeout(() => {
        onTimeout();
      }, SessionManager.TIMEOUT_DURATION);
    };

    const events = ['mousedown', 'keypress', 'scroll', 'touchstart'];
    events.forEach(event => {
      document.addEventListener(event, resetTimer, true);
    });

    resetTimer();

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(warningId);
      events.forEach(event => {
        document.removeEventListener(event, resetTimer, true);
      });
    };
  }
};

// Daten-Validierung
const DataValidator = {
  validateOrder: (order: any) => {
    const errors = [];
    
    if (!order.customerName?.trim()) {
      errors.push('Kundenname fehlt');
    }
    if (!order.vehicle?.trim()) {
      errors.push('Fahrzeug fehlt');
    }
    if (!order.assignedTo || order.assignedTo.length === 0) {
      errors.push('Keine Zuweisung vorhanden');
    }
    
    return {
      isValid: errors.length === 0,
      errors: errors
    };
  },

  validateInput: (field: string, value: string) => {
    const errors: any = {};
    
    switch(field) {
      case 'customerName':
        if (!value.trim()) errors.customerName = 'Kundenname erforderlich';
        if (value.length > 100) errors.customerName = 'Maximal 100 Zeichen';
        break;
        
      case 'vehicle':
        if (!value.trim()) errors.vehicle = 'Fahrzeug erforderlich';
        if (value.length > 100) errors.vehicle = 'Maximal 100 Zeichen';
        break;
        
      case 'hoursToAdd':
        const hours = parseFloat(value);
        if (isNaN(hours) || hours <= 0) errors.hours = 'Ungültige Stundenzahl';
        if (hours > 24) errors.hours = 'Maximal 24 Stunden pro Eintrag';
        break;
    }
    
    return errors;
  }
};

// Offline-Indikator Hook
const useOnlineStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
};

// Export/Import Funktionen
const DataExport = {
  exportAsJSON: (data: any, filename = 'werkstatt_backup') => {
    const exportData = {
      version: '1.0',
      exportDate: new Date().toISOString(),
      data: data
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  },

  importFromJSON: (file: File) => {
    return new Promise<any[]>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const imported = JSON.parse(e.target?.result as string);
          if (imported.version && imported.data) {
            resolve(imported.data);
          } else {
            reject(new Error('Ungültiges Dateiformat'));
          }
        } catch (error) {
          reject(error);
        }
      };
      reader.readAsText(file);
    });
  }
};

// =================== HAUPT-APP KOMPONENTEN ===================

// Checkbox Parser mit Prioritäten
function parseTasksWithCheckboxes(text: string, priority: string = 'medium') {
    if (!text) return [];
    
    const lines = text.split('\n');
    return lines.map((line, index) => {
        const checkboxMatch = line.match(/^(\s*)--([x]?)\s*(.*)$/);
        if (checkboxMatch) {
            return {
                id: `task-${index}`,
                type: 'checkbox',
                indent: checkboxMatch[1],
                checked: checkboxMatch[2] === 'x',
                text: checkboxMatch[3],
                originalLine: line,
                lineIndex: index,
                priority: priority
            };
        }
        return {
            id: `line-${index}`,
            type: 'text',
            text: line,
            originalLine: line,
            lineIndex: index,
            priority: priority
        };
    });
}

function updateTasksText(originalText: string, taskId: string, newChecked: boolean) {
    const lines = originalText.split('\n');
    const taskIndex = parseInt(taskId.split('-')[1]);
    
    if (lines[taskIndex]) {
        const line = lines[taskIndex];
        const checkboxMatch = line.match(/^(\s*)--([x]?)\s*(.*)$/);
        if (checkboxMatch) {
            const [, indent, , text] = checkboxMatch;
            lines[taskIndex] = `${indent}--${newChecked ? 'x' : ''} ${text}`;
        }
    }
    
    return lines.join('\n');
}

interface TaskRendererProps {
    tasks: any[];
    originalText: string;
    priority?: string;
    onTaskToggle: (taskId: string, checked: boolean) => void;
    onEditStart: () => void;
    onPriorityChange?: (priority: string) => void;
    showEditButton?: boolean;
    isChef?: boolean;
}

function TaskRenderer({ tasks, originalText, priority = 'medium', onTaskToggle, onEditStart, onPriorityChange, showEditButton = true, isChef = false }: TaskRendererProps) {
    const getPriorityColor = (priority: string) => {
        switch(priority) {
            case 'high': return 'border-red-500 bg-red-50';
            case 'low': return 'border-gray-300 bg-gray-50';
            default: return 'border-yellow-500 bg-yellow-50';
        }
    };

    const getPriorityLabel = (priority: string) => {
        switch(priority) {
            case 'high': return '🔴 Wichtig';
            case 'low': return '🟢 Niedrig';
            default: return '🟡 Mittel';
        }
    };

    if (!tasks || tasks.length === 0) {
        return (
            <div className={`p-4 rounded-lg border-2 ${getPriorityColor(priority)}`}>
                <p className="text-sm text-gray-500">
                    {isChef ? 'Klicken zum Bearbeiten...' : 'Keine Aufgaben definiert'}
                </p>
                {showEditButton && (
                    <button 
                        onClick={onEditStart}
                        className="text-xs text-blue-600 mt-2"
                    >
                        ✏️ Aufgaben {isChef ? 'bearbeiten' : 'hinzufügen'}
                    </button>
                )}
            </div>
        );
    }

    const completedTasks = tasks.filter(t => t.type === 'checkbox' && t.checked).length;
    const totalTasks = tasks.filter(t => t.type === 'checkbox').length;

    return (
        <div className={`p-4 rounded-lg border-2 ${getPriorityColor(priority)}`}>
            {isChef && onPriorityChange && (
                <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs font-medium text-gray-600">Priorität:</span>
                    <select
                        value={priority}
                        onChange={(e) => onPriorityChange(e.target.value)}
                        className="text-xs border border-gray-300 rounded px-2 py-1"
                    >
                        <option value="high">🔴 Wichtig</option>
                        <option value="medium">🟡 Mittel</option>
                        <option value="low">🟢 Niedrig</option>
                    </select>
                </div>
            )}

            {totalTasks > 0 && (
                <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
                    <span className="text-xs font-medium text-gray-600">
                        Fortschritt: {completedTasks}/{totalTasks} Aufgaben
                    </span>
                    <div className="flex items-center gap-2">
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                            <div 
                                className="bg-green-500 h-2 rounded-full transition-all" 
                                style={{ width: `${totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0}%` }}
                            />
                        </div>
                        <span className="text-xs text-gray-500">
                            {totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0}%
                        </span>
                    </div>
                </div>
            )}
            
            <div className="space-y-2">
                {tasks.map(task => (
                    <div key={task.id} className="flex items-start gap-2">
                        {task.type === 'checkbox' ? (
                            <>
                                <input
                                    type="checkbox"
                                    checked={task.checked}
                                    onChange={(e) => onTaskToggle(task.id, e.target.checked)}
                                    className="mt-0.5 rounded"
                                />
                                <span className={`text-sm flex-1 ${task.checked ? 'line-through text-gray-500' : 'text-gray-700'}`}>
                                    {task.text}
                                </span>
                                {task.checked && <span className="text-green-500 text-sm">✅</span>}
                            </>
                        ) : (
                            <span className="text-sm text-gray-700 ml-6">{task.text}</span>
                        )}
                    </div>
                ))}
            </div>
            
            {showEditButton && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                    <button 
                        onClick={onEditStart}
                        className="text-xs text-blue-600"
                    >
                        ✏️ Klicken zum Bearbeiten
                    </button>
                </div>
            )}
        </div>
    );
}

const MITARBEITER = ['Darek', 'Erhan'];

// HAUPT CHEF-APP KOMPONENTE
export default function ChefApp() {
    // Authentication States
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [currentUser, setCurrentUser] = useState('');
    const [loading, setLoading] = useState(true);
    
    // App States
    const [orders, setOrders] = useState<any[]>([]);
    const [dailyHours, setDailyHours] = useState<any[]>([]);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [messages, setMessages] = useState<any[]>([]);
    
    // Sicherheits-States
    const [showDeletedOrders, setShowDeletedOrders] = useState(false);
    const [sessionWarning, setSessionWarning] = useState(false);
    const [showRecoveryModal, setShowRecoveryModal] = useState(false);
    const { confirm, simpleConfirm } = useConfirmDialog();
    const isOnline = useOnlineStatus();
    
    // Bestehende States
    const [showForm, setShowForm] = useState(false);
    const [showMessageModal, setShowMessageModal] = useState(false);
    const [showDailyHoursModal, setShowDailyHoursModal] = useState(false);
    const [formData, setFormData] = useState({
        customerName: '', 
        vehicle: '', 
        tasks: '', 
        tasksPriority: 'medium', // NEU: Standard-Priorität
        materials: [{ id: Date.now(), quantity: '', name: '' }], 
        assignedTo: [] as string[]
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [showArchived, setShowArchived] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState<any>(null);
    const [viewMode, setViewMode] = useState('kanban');
    const [validationErrors, setValidationErrors] = useState<any>({});

    // Session prüfen
    useEffect(() => {
        const auth = sessionStorage.getItem('authenticated');
        const user = sessionStorage.getItem('user');
        if (auth === 'true' && user) {
            setIsAuthenticated(true);
            setCurrentUser(user);
        }
        setLoading(false);
    }, []);

    // ✅ KRITISCH KORRIGIERTE Firebase Listener
    useEffect(() => {
        if (!isAuthenticated) return;

        let mounted = true;

        // Orders Listener mit Validierung
        const ordersRef = ref(database, StorageManager.KEYS.ORDERS);
        const unsubscribeOrders = onValue(ordersRef, (snapshot) => {
            if (!mounted) return;
            
            console.log('Firebase Orders Update empfangen:', new Date().toISOString());
            
            if (snapshot.exists()) {
                const ordersObj = snapshot.val();
                const ordersArray = Object.values(ordersObj).map((order: any) => {
                    // Validiere und bereinige Order-Daten
                    return {
                        ...order,
                        id: order.id || 'unknown',
                        timeEntries: Array.isArray(order.timeEntries) ? order.timeEntries : [],
                        materials: Array.isArray(order.materials) ? order.materials : [],
                        photos: Array.isArray(order.photos) ? order.photos : [],
                        assignedTo: Array.isArray(order.assignedTo) ? order.assignedTo : [],
                        tasksPriority: order.tasksPriority || 'medium'
                    };
                });
                console.log('Anzahl Orders von Firebase:', ordersArray.length);
                setOrders(ordersArray);
            } else {
                console.log('Keine Orders in Firebase gefunden');
                setOrders([]);
            }
            setDataLoaded(true);
        }, (error) => {
            console.error('Firebase Orders Fehler:', error);
            alert('Fehler beim Laden der Aufträge. Bitte Seite neu laden.');
            setDataLoaded(true);
        });

        // ✅ KRITISCH KORRIGIERTER Daily Hours Listener (wie in Mitarbeiter-App!)
        const hoursRef = ref(database, StorageManager.KEYS.DAILY_HOURS);
        const unsubscribeHours = onValue(hoursRef, (snapshot) => {
            if (!mounted) return;
            
            console.log('Firebase DailyHours Update empfangen:', new Date().toISOString());
            
            if (snapshot.exists()) {
                const data = snapshot.val();
                
                if (Array.isArray(data) && data.length > 0) {
                    console.log(`DailyHours geladen: ${data.length} Einträge`);
                    
                    // Validiere jeden Eintrag
                    const validEntries = data.filter((entry: any) => 
                        entry && 
                        entry.employee && 
                        entry.date && 
                        typeof entry.hours === 'number' && 
                        entry.hours > 0 && 
                        entry.hours <= 24 &&
                        entry.id // ID muss vorhanden sein
                    );
                    
                    if (validEntries.length > 0) {
                        setDailyHours(prevHours => {
                            // Nur updaten wenn sich wirklich was geändert hat
                            if (deepEqual(prevHours, validEntries)) {
                                console.log('DailyHours unverändert');
                                return prevHours;
                            }
                            
                            console.log(`DailyHours Update: ${validEntries.length} gültige Einträge`);
                            return validEntries;
                        });
                    } else {
                        console.warn('Keine gültigen DailyHours Einträge gefunden - behalte lokalen State');
                        // WICHTIG: NICHT auf leeres Array setzen!
                    }
                } else if (Array.isArray(data) && data.length === 0) {
                    // Firebase hat explizit ein leeres Array
                    console.log('DailyHours ist leer in Firebase');
                    // Nur setzen wenn lokaler State auch leer ist
                    setDailyHours(prev => {
                        if (prev.length === 0) {
                            return []; // Bereits leer, OK
                        } else {
                            console.warn('Firebase hat leeres Array aber lokaler State hat Daten - behalte lokalen State!');
                            return prev; // Behalte lokale Daten!
                        }
                    });
                } else {
                    console.warn('DailyHours hat unerwartetes Format:', typeof data);
                    // NICHTS machen!
                }
            } else {
                // KRITISCH: Firebase hat keine Daten (null/undefined)
                console.log('Keine DailyHours in Firebase - behalte lokalen State');
                // NIEMALS auf leeres Array setzen!
                // setDailyHours([]); // ❌ NIEMALS!
            }
        }, (error) => {
            console.error('Firebase DailyHours Fehler:', error);
            // Bei Fehler nichts ändern!
        });

        // Messages Listener
        const messagesRef = ref(database, StorageManager.KEYS.MESSAGES);
        const unsubscribeMessages = onValue(messagesRef, (snapshot) => {
            if (!mounted) return;
            
            if (snapshot.exists()) {
                const data = snapshot.val();
                if (Array.isArray(data)) {
                    setMessages(data);
                } else {
                    console.warn('Messages hat unerwartetes Format');
                }
            } else {
                console.log('Keine Messages in Firebase gefunden');
                // Nicht auf leeres Array setzen!
            }
        }, (error) => {
            console.error('Firebase Messages Fehler:', error);
        });

        return () => {
            mounted = false;
            unsubscribeOrders();
            unsubscribeHours();
            unsubscribeMessages();
        };
    }, [isAuthenticated]);

    // Session-Timeout implementieren
    useEffect(() => {
        const cleanup = SessionManager.init(
            () => {
                alert('Ihre Sitzung ist abgelaufen. Bitte laden Sie die Seite neu.');
                handleLogout();
            },
            () => {
                setSessionWarning(true);
                setTimeout(() => setSessionWarning(false), 10000);
            }
        );

        return cleanup;
    }, []);

    // Logout Funktion
    const handleLogout = () => {
        sessionStorage.clear();
        setIsAuthenticated(false);
        setCurrentUser('');
    };

    // Berechne Statistiken
    const calculateTotalHours = (timeEntries: any[]) => {
        if (!timeEntries || !Array.isArray(timeEntries)) return 0;
        return timeEntries.reduce((acc, entry) => acc + (entry.hours || 0), 0);
    };

    // Form Handlers
    const handleFormChange = (field: string, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        
        // Live-Validierung
        const errors = DataValidator.validateInput(field, value);
        setValidationErrors((prev: any) => ({ ...prev, ...errors }));
    };
    
    const handleMaterialChange = (id: number, field: string, value: string) => {
        const materials = [...formData.materials];
        const index = materials.findIndex(m => m.id === id);
        if (index !== -1) {
            materials[index] = { ...materials[index], [field]: value };
            setFormData(prev => ({ ...prev, materials }));
        }
    };
    
    const handleAssignmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value, checked } = e.target;
        let assigned = [...formData.assignedTo];
        if (checked) {
            assigned.push(value);
        } else {
            assigned = assigned.filter(emp => emp !== value);
        }
        setFormData(prev => ({ ...prev, assignedTo: assigned }));
    };

    const addMaterialRow = () => setFormData(prev => ({
        ...prev, 
        materials: [...prev.materials, { id: Date.now() + Math.random(), quantity: '', name: '' }]
    }));
    
    const removeMaterialRow = (id: number) => {
        if (formData.materials.length > 1) {
            setFormData(prev => ({
                ...prev, 
                materials: formData.materials.filter(m => m.id !== id)
            }));
        }
    };

    // Neuen Auftrag hinzufügen mit Priorität
    const handleAddOrder = async () => {
        try {
            const newOrderId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            const newOrder = {
                customerName: formData.customerName, 
                vehicle: formData.vehicle, 
                tasks: formData.tasks, 
                tasksPriority: formData.tasksPriority, // NEU
                materials: formData.materials.filter(m => m.name.trim() !== ''), 
                assignedTo: formData.assignedTo,
                status: 'Neu', 
                createdAt: new Date().toISOString(),
                deleted: false,
                statusHistory: [{ status: 'Neu', timestamp: new Date().toISOString(), by: currentUser }],
                timeEntries: [], 
                photos: [],
                documents: []
            };

            // Validierung
            const validation = DataValidator.validateOrder(newOrder);
            if (!validation.isValid) {
                alert(`Bitte korrigieren Sie folgende Fehler:\n${validation.errors.join('\n')}`);
                return;
            }
            
            const success = await StorageManager.saveOrder(newOrderId, newOrder, currentUser);
            
            if (success) {
                // Reset form
                setFormData({
                    customerName: '', 
                    vehicle: '', 
                    tasks: '', 
                    tasksPriority: 'medium',
                    materials: [{ id: Date.now(), quantity: '', name: '' }], 
                    assignedTo: []
                });
                setValidationErrors({});
                setShowForm(false);
            } else {
                alert('Fehler beim Erstellen des Auftrags');
            }
            
        } catch (error: any) {
            alert(`Fehler beim Erstellen des Auftrags: ${error.message}`);
        }
    };

    // Status ändern mit Versionskontrolle
    const handleStatusChange = async (orderId: string, newStatus: string) => {
        const order = orders.find(o => o.id === orderId);
        if (!order) return;

        const success = await StorageManager.saveOrder(
            orderId,
            { 
                status: newStatus,
                statusHistory: [
                    ...(order.statusHistory || []),
                    { status: newStatus, timestamp: new Date().toISOString(), by: currentUser }
                ],
                _expectedVersion: order.version
            },
            currentUser
        );
        
        if (!success) {
            alert('Fehler beim Ändern des Status. Bitte Seite neu laden.');
        }
    };

    // Auftrag löschen mit Backup
    const handleDeleteOrder = async (orderId: string) => {
        const order = orders.find(o => o.id === orderId);
        if (!order) return;

        const confirmed = await confirm(
            `Möchten Sie den Auftrag "${order.customerName} - ${order.vehicle}" wirklich löschen?`,
            'LÖSCHEN'
        );

        if (confirmed) {
            // Erstelle Backup vor dem Löschen
            await StorageManager.createBackup(orderId, order, 'pre-delete');
            
            const deletedOrder = SoftDelete.markAsDeleted(order, currentUser);
            
            const success = await StorageManager.saveOrder(
                orderId,
                deletedOrder,
                currentUser
            );
            
            if (!success) {
                alert('Fehler beim Löschen des Auftrags');
            }
        }
    };

    // Auftrag wiederherstellen
    const handleRestoreOrder = async (orderId: string) => {
        const confirmed = await simpleConfirm('Möchten Sie diesen Auftrag wiederherstellen?');
        
        if (confirmed) {
            const order = orders.find(o => o.id === orderId);
            if (order) {
                const restoredOrder = SoftDelete.restore(order);
                
                const success = await StorageManager.saveOrder(
                    orderId,
                    restoredOrder,
                    currentUser
                );
                
                if (!success) {
                    alert('Fehler beim Wiederherstellen des Auftrags');
                }
            }
        }
    };

    // Export-Funktion
    const handleExport = () => {
        DataExport.exportAsJSON(orders, 'werkstatt_auftraege');
    };

    // Import-Funktion
    const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            const importedData = await DataExport.importFromJSON(file);
            const confirmed = await simpleConfirm(
                `Möchten Sie ${importedData.length} Aufträge importieren? Dies überschreibt alle aktuellen Daten!`
            );
            
            if (confirmed) {
                // Backup vor Import
                for (const order of orders) {
                    await StorageManager.createBackup(order.id, order, 'pre-import');
                }
                
                // Importiere jeden Auftrag einzeln
                for (const order of importedData) {
                    await StorageManager.saveOrder(order.id, order, currentUser);
                }
                
                alert('Import erfolgreich!');
            }
        } catch (error: any) {
            alert(`Fehler beim Import: ${error.message}`);
        }
        
        e.target.value = ''; // Reset input
    };

    // ✅ KRITISCH KORRIGIERT: Handler für DailyHours Speicherung (nutzt upsertDailyHour!)
    const handleSaveDailyHours = async (hours: number, date: string, employee: string) => {
        console.log(`Chef speichert: ${hours}h für ${employee} am ${date}`);
        
        // VERWENDE DIE NEUE UPSERT METHODE!
        const success = await StorageManager.upsertDailyHour(employee, date, hours);
        
        if (success) {
            // Lokalen State nur updaten wenn erfolgreich
            const updatedDailyHours = [...dailyHours];
            const existingIndex = updatedDailyHours.findIndex(entry => 
                entry.employee === employee && entry.date === date
            );
            
            if (existingIndex >= 0) {
                updatedDailyHours[existingIndex] = {
                    ...updatedDailyHours[existingIndex],
                    hours,
                    updatedAt: new Date().toISOString()
                };
            } else {
                updatedDailyHours.push({
                    id: `${Date.now()}_${employee}_${Math.random().toString(36).substr(2, 9)}`,
                    employee,
                    date,
                    hours,
                    createdAt: new Date().toISOString()
                });
            }
            
            setDailyHours(updatedDailyHours);
            console.log('DailyHours erfolgreich gespeichert');
        } else {
            alert('Fehler beim Speichern der Arbeitszeiten. Bitte erneut versuchen.');
        }
    };

    // Filter anwenden mit Prioritätssortierung
    const getFilteredOrders = () => {
        let filtered = showDeletedOrders 
            ? SoftDelete.filterDeleted(orders)
            : SoftDelete.filterActive(orders);
        
        if (!showArchived && !showDeletedOrders) {
            filtered = filtered.filter(order => order.status !== 'Archiviert');
        }
        
        if (searchQuery.trim()) {
            const searchTerms = searchQuery.toLowerCase().split(' ').filter(term => term);
            filtered = filtered.filter(order => {
                const searchableString = [
                    order.customerName,
                    order.vehicle,
                    order.tasks,
                    ...(order.materials?.map((m: any) => m.name) || [])
                ].join(' ').toLowerCase();
                
                return searchTerms.every(term => searchableString.includes(term));
            });
        }
        
        // Sortiere nach Priorität
        filtered.sort((a, b) => {
            const priorityOrder = { high: 0, medium: 1, low: 2 };
            const aPriority = priorityOrder[a.tasksPriority as keyof typeof priorityOrder] ?? 1;
            const bPriority = priorityOrder[b.tasksPriority as keyof typeof priorityOrder] ?? 1;
            return aPriority - bPriority;
        });
        
        return filtered;
    };

    const filteredOrders = getFilteredOrders();

    // Sichere Update-Funktion für Aufträge
    const updateOrderSafely = async (orderId: string, updates: any) => {
        try {
            const order = orders.find(o => o.id === orderId);
            if (!order) return false;
            
            const success = await StorageManager.saveOrder(
                orderId, 
                { ...updates, _expectedVersion: order.version }, 
                currentUser
            );
            
            if (!success) {
                if (await simpleConfirm('Die Daten wurden zwischenzeitlich geändert. Seite neu laden?')) {
                    window.location.reload();
                }
            }
            
            return success;
        } catch (error: any) {
            if (error.message === 'VERSION_CONFLICT') {
                alert('Die Daten wurden von einem anderen Benutzer geändert. Bitte laden Sie die Seite neu.');
            } else {
                alert(`Fehler beim Aktualisieren: ${error.message}`);
            }
            return false;
        }
    };

    // Loading Screen
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-xl">Lade...</div>
            </div>
        );
    }

    // PIN Login anzeigen wenn nicht authentifiziert
    if (!isAuthenticated) {
        return (
            <PinLogin 
                onSuccess={(user) => {
                    setIsAuthenticated(true);
                    setCurrentUser(user);
                }} 
                isChef={true}
                authenticate={authenticateChef}
            />
        );
    }

    // Firebase Daten laden
    if (!dataLoaded) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Lade Daten...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gray-100 min-h-screen font-sans">
            {/* Offline-Indikator */}
            {!isOnline && (
                <div className="fixed top-0 left-0 right-0 bg-yellow-500 text-white text-center py-2 z-50">
                    ⚠️ Offline-Modus - Änderungen werden lokal gespeichert
                </div>
            )}

            {/* Session Warning */}
            {sessionWarning && (
                <div className="fixed top-4 right-4 bg-yellow-100 border border-yellow-400 p-4 rounded-lg shadow-lg z-50 animate-pulse">
                    <p className="text-yellow-800 font-semibold">
                        ⚠️ Ihre Sitzung läuft in 5 Minuten ab!
                    </p>
                </div>
            )}

            {/* Header */}
            <header className="bg-white shadow-md">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-3">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-800">
                                Auftrags-App - Chef Dashboard
                            </h1>
                            <div className="flex items-center gap-4 text-xs text-gray-500">
                                <span>Angemeldet als: {currentUser}</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button 
                                onClick={() => setShowDailyHoursModal(true)} 
                                className="text-sm px-3 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200"
                            >
                                🕐 Arbeitszeiten
                            </button>
                            <button 
                                onClick={() => setShowDeletedOrders(!showDeletedOrders)}
                                className={`text-sm px-3 py-1 rounded ${
                                    showDeletedOrders 
                                        ? 'bg-red-100 text-red-700' 
                                        : 'bg-gray-100 text-gray-700'
                                } hover:opacity-80`}
                            >
                                🗑️ {showDeletedOrders ? 'Normale Ansicht' : 'Gelöschte'}
                                {showDeletedOrders && ` (${SoftDelete.filterDeleted(orders).length})`}
                            </button>
                            <button
                                onClick={() => setShowRecoveryModal(true)}
                                className="text-sm px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                            >
                                🔄 Backup
                            </button>
                            <button
                                onClick={handleExport}
                                className="text-sm px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                            >
                                📥 Export
                            </button>
                            <label className="text-sm px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 cursor-pointer">
                                📤 Import
                                <input
                                    type="file"
                                    accept=".json"
                                    onChange={handleImport}
                                    className="hidden"
                                />
                            </label>
                            <button 
                                onClick={() => setShowMessageModal(true)} 
                                className="flex items-center gap-2 text-sm font-semibold text-white bg-blue-600 px-4 py-2 rounded-md hover:bg-blue-700"
                            >
                                <span>📨</span>
                                Nachricht senden
                            </button>
                            <button 
                                onClick={handleLogout}
                                className="text-sm bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
                            >
                                🚪 Abmelden
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Suchleiste und Controls */}
            <div className="bg-white border-b">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex justify-between items-center gap-4">
                        <input 
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Aufträge durchsuchen..."
                            className="px-4 py-2 border border-gray-300 rounded-lg w-full md:w-1/3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="flex items-center gap-4">
                            <div className="flex bg-gray-100 rounded-lg p-1">
                                <button 
                                    onClick={() => setViewMode('kanban')}
                                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                                        viewMode === 'kanban' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'
                                    }`}
                                >
                                    📋 Kanban
                                </button>
                                <button 
                                    onClick={() => setViewMode('table')}
                                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                                        viewMode === 'table' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'
                                    }`}
                                >
                                    📊 Tabelle
                                </button>
                                <button 
                                    onClick={() => setViewMode('hours')}
                                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                                        viewMode === 'hours' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'
                                    }`}
                                >
                                    🕐 Arbeitszeiten
                                </button>
                            </div>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input 
                                    type="checkbox" 
                                    checked={showArchived} 
                                    onChange={() => setShowArchived(!showArchived)} 
                                />
                                <span className="text-sm">Archiv anzeigen</span>
                            </label>
                            <button 
                                onClick={() => setShowForm(!showForm)} 
                                className="px-6 py-2 bg-indigo-600 text-white font-semibold rounded-lg shadow-md hover:bg-indigo-700 whitespace-nowrap"
                            >
                                {showForm ? 'Formular schließen' : '+ Neuer Auftrag'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Formular mit Priorität */}
            {showForm && (
                <div className="bg-white border-b">
                    <div className="max-w-4xl mx-auto p-8">
                        <div className="space-y-6">
                            <h2 className="text-xl font-semibold text-gray-800">Neuer Auftrag</h2>
                            
                            <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Kundenname <span className="text-red-500">*</span>
                                    </label>
                                    <input 
                                        type="text" 
                                        value={formData.customerName || ''} 
                                        onChange={(e) => handleFormChange('customerName', e.target.value)} 
                                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                            validationErrors.customerName ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                    />
                                    {validationErrors.customerName && (
                                        <p className="text-red-500 text-xs mt-1">{validationErrors.customerName}</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Fahrzeug <span className="text-red-500">*</span>
                                    </label>
                                    <input 
                                        type="text" 
                                        value={formData.vehicle || ''} 
                                        onChange={(e) => handleFormChange('vehicle', e.target.value)} 
                                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                            validationErrors.vehicle ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                    />
                                    {validationErrors.vehicle && (
                                        <p className="text-red-500 text-xs mt-1">{validationErrors.vehicle}</p>
                                    )}
                                </div>
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Zuweisen an: <span className="text-red-500">*</span>
                                </label>
                                <div className="flex gap-4 mt-2">
                                    {MITARBEITER.map(emp => (
                                        <label key={emp} className="flex items-center gap-2">
                                            <input 
                                                type="checkbox" 
                                                value={emp} 
                                                checked={formData.assignedTo.includes(emp)} 
                                                onChange={handleAssignmentChange} 
                                            />
                                            <span className="text-sm">{emp}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                            
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Arbeitsbeschreibung
                                    </label>
                                    <select
                                        value={formData.tasksPriority}
                                        onChange={(e) => handleFormChange('tasksPriority', e.target.value)}
                                        className="text-sm border border-gray-300 rounded px-2 py-1"
                                    >
                                        <option value="high">🔴 Wichtig</option>
                                        <option value="medium">🟡 Mittel</option>
                                        <option value="low">🟢 Niedrig</option>
                                    </select>
                                </div>
                                <textarea 
                                    value={formData.tasks || ''} 
                                    onChange={(e) => handleFormChange('tasks', e.target.value)} 
                                    rows={6} 
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Aufgaben eingeben...&#10;&#10;Einfache Checkboxen:&#10;-- Ölwechsel&#10;-- Bremsbeläge prüfen&#10;-- TÜV-Vorbereitung&#10;&#10;Normaler Text ohne --"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    💡 <strong>Super einfach:</strong> Schreibe <code>--</code> für Checkboxen, z.B. "-- Ölwechsel"
                                </p>
                            </div>
                            
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="block text-sm font-medium text-gray-700">Material</label>
                                    <button 
                                        onClick={addMaterialRow} 
                                        className="text-sm bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                                    >
                                        + Zeile hinzufügen
                                    </button>
                                </div>
                                <div className="space-y-2">
                                    {formData.materials.map((item) => (
                                        <div key={item.id} className="flex items-center gap-2">
                                            <input 
                                                type="text" 
                                                placeholder="Menge" 
                                                value={item.quantity || ''} 
                                                onChange={(e) => handleMaterialChange(item.id, 'quantity', e.target.value)} 
                                                className="w-24 px-3 py-2 border border-gray-300 rounded-md"
                                            />
                                            <input 
                                                type="text" 
                                                placeholder="Artikelbeschreibung" 
                                                value={item.name || ''} 
                                                onChange={(e) => handleMaterialChange(item.id, 'name', e.target.value)} 
                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                                            />
                                            {formData.materials.length > 1 && (
                                                <button 
                                                    onClick={() => removeMaterialRow(item.id)} 
                                                    className="text-red-500 hover:text-red-700 px-2 py-2"
                                                >
                                                    ✕
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            
                            <div className="flex justify-end pt-4 border-t">
                                <button 
                                    onClick={handleAddOrder}
                                    className="bg-blue-600 text-white px-6 py-2 rounded-md font-semibold hover:bg-blue-700"
                                >
                                    Auftrag speichern
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Main Content */}
            <main>
                <div className="max-w-7xl mx-auto p-4 md:p-8">
                    {showDeletedOrders ? (
                        <DeletedOrdersView 
                            orders={SoftDelete.filterDeleted(orders)}
                            onRestore={handleRestoreOrder}
                            onPermanentDelete={async (orderId) => {
                                const confirmed = await confirm(
                                    'ACHTUNG: Dieser Auftrag wird ENDGÜLTIG gelöscht! Diese Aktion kann nicht rückgängig gemacht werden.',
                                    'ENDGÜLTIG LÖSCHEN'
                                );
                                if (confirmed) {
                                    // Entferne aus Firebase
                                    await set(ref(database, `${StorageManager.KEYS.ORDERS}/${orderId}`), null);
                                }
                            }}
                        />
                    ) : viewMode === 'kanban' ? (
                        <div className="flex flex-col xl:flex-row gap-6">
                            <OrderColumn 
                                title="Neu" 
                                status="Neu" 
                                bgColor="bg-blue-400" 
                                orders={filteredOrders}
                                onStatusChange={handleStatusChange}
                                onOrderSelect={setSelectedOrder}
                                onDeleteOrder={handleDeleteOrder}
                                calculateTotalHours={calculateTotalHours}
                            />
                            <OrderColumn 
                                title="In Arbeit" 
                                status="In Arbeit" 
                                bgColor="bg-yellow-400" 
                                orders={filteredOrders}
                                onStatusChange={handleStatusChange}
                                onOrderSelect={setSelectedOrder}
                                onDeleteOrder={handleDeleteOrder}
                                calculateTotalHours={calculateTotalHours}
                            />
                            <OrderColumn 
                                title="Erledigt" 
                                status="Erledigt" 
                                bgColor="bg-green-400" 
                                orders={filteredOrders}
                                onStatusChange={handleStatusChange}
                                onOrderSelect={setSelectedOrder}
                                onDeleteOrder={handleDeleteOrder}
                                calculateTotalHours={calculateTotalHours}
                            />
                            {showArchived && (
                                <OrderColumn 
                                    title="Archiv" 
                                    status="Archiviert" 
                                    bgColor="bg-gray-400" 
                                    orders={filteredOrders}
                                    onStatusChange={handleStatusChange}
                                    onOrderSelect={setSelectedOrder}
                                    onDeleteOrder={handleDeleteOrder}
                                    calculateTotalHours={calculateTotalHours}
                                />
                            )}
                        </div>
                    ) : viewMode === 'table' ? (
                        <TableView 
                            orders={filteredOrders}
                            onStatusChange={handleStatusChange}
                            onOrderSelect={setSelectedOrder}
                            onDeleteOrder={handleDeleteOrder}
                            calculateTotalHours={calculateTotalHours}
                        />
                    ) : (
                        <WorkingHoursView 
                            dailyHours={dailyHours}
                            orders={orders}
                        />
                    )}
                </div>
            </main>

            {/* Modals */}
            {selectedOrder && (
                <OrderDetailModal 
                    order={selectedOrder}
                    orders={orders}
                    setOrders={setOrders}
                    onClose={() => setSelectedOrder(null)}
                    calculateTotalHours={calculateTotalHours}
                    updateOrderSafely={updateOrderSafely}
                    currentUser={currentUser}
                />
            )}
            
            {showMessageModal && (
                <MessageModal 
                    onClose={() => setShowMessageModal(false)}
                    messages={messages}
                    setMessages={async (newMessages) => {
                        setMessages(newMessages);
                        await StorageManager.saveMessages(newMessages);
                    }}
                />
            )}
            
            {showDailyHoursModal && (
                <ChefDailyHoursModal
                    onClose={() => setShowDailyHoursModal(false)}
                    dailyHours={dailyHours}
                    onSave={handleSaveDailyHours}
                />
            )}
            
            {showRecoveryModal && (
                <RecoveryModal 
                    onClose={() => setShowRecoveryModal(false)}
                    onRestore={async (orderId, backupId) => {
                        const success = await StorageManager.restoreFromBackup(orderId, backupId);
                        if (success) {
                            alert('Wiederherstellung erfolgreich!');
                            setShowRecoveryModal(false);
                        } else {
                            alert('Fehler bei der Wiederherstellung');
                        }
                    }}
                    orders={orders}
                />
            )}
        </div>
    );
}

// KOMPONENTEN

interface OrderColumnProps {
    title: string;
    status: string;
    bgColor: string;
    orders: any[];
    onStatusChange: (orderId: string, newStatus: string) => void;
    onOrderSelect: (order: any) => void;
    onDeleteOrder: (orderId: string) => void;
    calculateTotalHours: (timeEntries: any[]) => number;
}

function OrderColumn({ title, status, bgColor, orders, onStatusChange, onOrderSelect, onDeleteOrder, calculateTotalHours }: OrderColumnProps) {
    const ordersForColumn = orders.filter(order => order.status === status);
    
    const getPriorityBadge = (priority: string) => {
        switch(priority) {
            case 'high': return '🔴';
            case 'low': return '🟢';
            default: return '🟡';
        }
    };
    
    const OrderCard = ({ order }: { order: any }) => {
        const tasksData = parseTasksWithCheckboxes(order.tasks, order.tasksPriority);
        const completedTasks = tasksData.filter(t => t.type === 'checkbox' && t.checked).length;
        const totalTasks = tasksData.filter(t => t.type === 'checkbox').length;
        
        return (
            <div className="bg-white rounded-lg shadow-md p-4 mb-4 hover:shadow-lg transition-shadow cursor-pointer">
                <div onClick={() => onOrderSelect(order)}>
                    <div className="flex items-start justify-between">
                        <div className="flex-1">
                            <div className="flex items-center gap-2">
                                <h3 className="font-bold text-lg text-gray-800">{order.customerName}</h3>
                                <span className="text-lg">{getPriorityBadge(order.tasksPriority || 'medium')}</span>
                            </div>
                            <p className="text-gray-600">{order.vehicle}</p>
                        </div>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onDeleteOrder(order.id);
                            }}
                            className="text-red-500 hover:text-red-700 p-1"
                            title="Löschen"
                        >
                            🗑️
                        </button>
                    </div>
                    {order.assignedTo && order.assignedTo.length > 0 && 
                        <p className="text-sm font-bold text-indigo-600 mt-1">
                            Zuständig: {order.assignedTo.join(', ')}
                        </p>
                    }
                    <p className="text-sm font-semibold text-gray-500 mt-1">
                        Arbeitsstunden: {calculateTotalHours(order.timeEntries).toFixed(2)}
                    </p>
                    
                    {/* Checkbox Progress */}
                    {totalTasks > 0 && (
                        <div className="mt-2">
                            <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                                <span>Aufgaben: {completedTasks}/{totalTasks}</span>
                                <span>{Math.round((completedTasks / totalTasks) * 100)}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-1.5">
                                <div 
                                    className="bg-green-500 h-1.5 rounded-full transition-all" 
                                    style={{ width: `${(completedTasks / totalTasks) * 100}%` }}
                                />
                            </div>
                        </div>
                    )}
                    
                    <div className="bg-gray-50 p-3 mt-3 rounded-md">
                        <p className="text-sm font-semibold text-gray-700">Aufgaben:</p>
                        <p className="text-sm text-gray-600 whitespace-pre-wrap line-clamp-3">{order.tasks}</p>
                    </div>
                    {order.materials && order.materials.length > 0 && (
                        <div className="bg-gray-100 p-3 mt-3 rounded-md">
                            <p className="text-sm font-semibold text-gray-700">Material:</p>
                            <ul className="list-disc list-inside text-sm text-gray-600 mt-1">
                                {order.materials.slice(0, 3).map((m: any, i: number) => (
                                    <li key={i}>{m.quantity}x {m.name}</li>
                                ))}
                                {order.materials.length > 3 && (
                                    <li className="text-gray-500">... und {order.materials.length - 3} weitere</li>
                                )}
                            </ul>
                        </div>
                    )}
                    {(order.photos?.length > 0 || order.documents?.length > 0) && (
                        <div className="mt-3 flex gap-2">
                            {order.photos?.length > 0 && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                                    📷 {order.photos.length} Foto{order.photos.length !== 1 ? 's' : ''}
                                </span>
                            )}
                            {order.documents?.length > 0 && (
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                                    📄 {order.documents.length} Dokument{order.documents.length !== 1 ? 'e' : ''}
                                </span>
                            )}
                        </div>
                    )}
                </div>
                
                <div className="mt-4 flex justify-end space-x-2" onClick={(e) => e.stopPropagation()}>
                    {order.status === 'Erledigt' && (
                        <button 
                            onClick={() => onStatusChange(order.id, 'Archiviert')} 
                            className="btn-status bg-gray-500 hover:bg-gray-600"
                        >
                            » Archiv
                        </button>
                    )}
                    {order.status !== 'Neu' && (
                        <button 
                            onClick={() => onStatusChange(order.id, 'Neu')} 
                            className="btn-status bg-blue-500 hover:bg-blue-600"
                        >
                            » Neu
                        </button>
                    )}
                    {order.status !== 'In Arbeit' && (
                        <button 
                            onClick={() => onStatusChange(order.id, 'In Arbeit')} 
                            className="btn-status bg-yellow-500 hover:bg-yellow-600"
                        >
                            » Arbeit
                        </button>
                    )}
                    {order.status !== 'Erledigt' && (
                        <button 
                            onClick={() => onStatusChange(order.id, 'Erledigt')} 
                            className="btn-status bg-green-500 hover:bg-green-600"
                        >
                            » Erledigt
                        </button>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className={`flex-1 min-w-80 rounded-xl ${bgColor}`}>
            <div className="p-4">
                <h2 className="text-xl font-bold mb-4 text-white text-center">
                    {title} ({ordersForColumn.length})
                </h2>
                <div className="min-h-96">
                    {ordersForColumn.length > 0 ? (
                        ordersForColumn.map(o => <OrderCard key={o.id} order={o} />)
                    ) : (
                        <p className="text-white/70 text-center py-8">Keine Aufträge.</p>
                    )}
                </div>
            </div>
        </div>
    );
}

interface TableViewProps {
    orders: any[];
    onStatusChange: (orderId: string, newStatus: string) => void;
    onOrderSelect: (order: any) => void;
    onDeleteOrder: (orderId: string) => void;
    calculateTotalHours: (timeEntries: any[]) => number;
}

function TableView({ orders, onStatusChange, onOrderSelect, onDeleteOrder, calculateTotalHours }: TableViewProps) {
    const getStatusBadge = (status: string) => {
        const styles: { [key: string]: string } = {
            'Neu': 'bg-blue-100 text-blue-800',
            'In Arbeit': 'bg-yellow-100 text-yellow-800',
            'Erledigt': 'bg-green-100 text-green-800',
            'Archiviert': 'bg-gray-100 text-gray-800'
        };
        return (
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status]}`}>
                {status}
            </span>
        );
    };

    const getPriorityBadge = (priority: string) => {
        switch(priority) {
            case 'high': return '🔴';
            case 'low': return '🟢';
            default: return '🟡';
        }
    };

    return (
        <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Priorität
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Kunde
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Fahrzeug
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Zugewiesen
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Stunden
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Material
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Medien
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Aktionen
                        </th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {orders.map(order => (
                        <tr key={order.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-center text-lg">
                                {getPriorityBadge(order.tasksPriority || 'medium')}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div 
                                    className="text-sm font-medium text-gray-900 cursor-pointer hover:text-blue-600"
                                    onClick={() => onOrderSelect(order)}
                                >
                                    {order.customerName}
                                </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {order.vehicle}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex gap-1">
                                    {order.assignedTo?.map((emp: string) => (
                                        <span key={emp} className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded">
                                            {emp}
                                        </span>
                                    ))}
                                </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                {getStatusBadge(order.status)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {calculateTotalHours(order.timeEntries).toFixed(1)}h
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {order.materials?.length || 0} Artikel
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                <div className="flex gap-2">
                                    {order.photos?.length > 0 && (
                                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                                            📷 {order.photos.length}
                                        </span>
                                    )}
                                    {order.documents?.length > 0 && (
                                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                                            📄 {order.documents.length}
                                        </span>
                                    )}
                                </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <div className="flex gap-2">
                                    <select 
                                        value={order.status}
                                        onChange={(e) => onStatusChange(order.id, e.target.value)}
                                        className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="Neu">Neu</option>
                                        <option value="In Arbeit">In Arbeit</option>
                                        <option value="Erledigt">Erledigt</option>
                                        <option value="Archiviert">Archiviert</option>
                                    </select>
                                    <button
                                        onClick={() => onDeleteOrder(order.id)}
                                        className="text-red-500 hover:text-red-700"
                                        title="Löschen"
                                    >
                                        🗑️
                                    </button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

interface WorkingHoursViewProps {
    dailyHours: any[];
    orders: any[];
}

function WorkingHoursView({ dailyHours, orders }: WorkingHoursViewProps) {
    // Get current month and year
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    // Get holidays for current year
    const holidays = getAustrianHolidays(currentYear);
    const holidayMap = new Map(holidays.map(h => [h.date, h.name]));
    
    // Calculate statistics
    const today = new Date().toISOString().split('T')[0];
    const todayEntries = dailyHours.filter(entry => entry.date === today);
    
    // Get last 30 days statistics
    const last30Days = new Date();
    last30Days.setDate(last30Days.getDate() - 30);
    const recentEntries = dailyHours.filter(entry => 
        new Date(entry.date) >= last30Days
    );
    
    // Group by employee
    const employeeStats = MITARBEITER.map(employee => {
        const employeeEntries = recentEntries.filter(e => e.employee === employee);
        const totalHours = employeeEntries.reduce((sum, e) => sum + e.hours, 0);
        const avgHours = employeeEntries.length > 0 ? totalHours / employeeEntries.length : 0;
        const todayHours = todayEntries.find(e => e.employee === employee)?.hours || 0;
        
        // Calculate project hours from orders
        const projectHours = orders.reduce((sum, order) => {
            const employeeTimeEntries = order.timeEntries?.filter((te: any) => te.employee === employee) || [];
            return sum + employeeTimeEntries.reduce((s: number, te: any) => s + te.hours, 0);
        }, 0);
        
        return {
            name: employee,
            todayHours,
            totalHours,
            avgHours,
            daysWorked: employeeEntries.length,
            projectHours,
            entries: employeeEntries
        };
    });
    
    // Get calendar data - FIX: Adjust for Monday start
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    // Adjust for Monday as first day (0 = Sunday, so we need to shift)
    const adjustedStart = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;
    
    const calendarDays: (number | null)[] = [];
    // Add empty cells for days before month starts
    for (let i = 0; i < adjustedStart; i++) {
        calendarDays.push(null);
    }
    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
        calendarDays.push(i);
    }
    
    return (
        <div className="space-y-6">
            {/* Today's Summary */}
            <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold mb-4">Heutige Arbeitszeiten</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {employeeStats.map(emp => (
                        <div key={emp.name} className="bg-gray-50 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="font-medium text-gray-800">{emp.name}</h3>
                                {emp.todayHours > 0 ? (
                                    <span className="text-green-600 font-bold text-lg">{emp.todayHours}h</span>
                                ) : (
                                    <span className="text-red-500 text-sm">Nicht eingetragen</span>
                                )}
                            </div>
                            {emp.todayHours === 0 && now.getHours() >= 17 && now.getMinutes() >= 30 && (
                                <div className="bg-red-100 text-red-700 text-xs p-2 rounded">
                                    ⚠️ Erinnerung: Arbeitszeit noch nicht eingetragen!
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
            
            {/* 30 Days Statistics */}
            <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold mb-4">Letzte 30 Tage Übersicht</h2>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b">
                                <th className="text-left py-2">Mitarbeiter</th>
                                <th className="text-center py-2">Heute</th>
                                <th className="text-center py-2">Ø pro Tag</th>
                                <th className="text-center py-2">Gesamt</th>
                                <th className="text-center py-2">Tage gearbeitet</th>
                                <th className="text-center py-2">Projekt-Stunden</th>
                            </tr>
                        </thead>
                        <tbody>
                            {employeeStats.map(emp => (
                                <tr key={emp.name} className="border-b">
                                    <td className="py-3 font-medium">{emp.name}</td>
                                    <td className="text-center">
                                        {emp.todayHours > 0 ? (
                                            <span className="text-green-600 font-medium">{emp.todayHours}h</span>
                                        ) : (
                                            <span className="text-gray-400">-</span>
                                        )}
                                    </td>
                                    <td className="text-center">{emp.avgHours.toFixed(1)}h</td>
                                    <td className="text-center font-medium">{emp.totalHours.toFixed(1)}h</td>
                                    <td className="text-center">{emp.daysWorked}</td>
                                    <td className="text-center text-blue-600">{emp.projectHours.toFixed(1)}h</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            
            {/* Monthly Calendar */}
            <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold mb-4">
                    {new Date(currentYear, currentMonth).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}
                </h2>
                
                {MITARBEITER.map(employee => (
                    <div key={employee} className="mb-6">
                        <h3 className="font-medium text-gray-800 mb-2">{employee}</h3>
                        <div className="grid grid-cols-7 gap-1">
                            {/* Updated weekday headers for Monday first */}
                            {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(day => (
                                <div key={day} className="text-center text-xs font-medium text-gray-500 p-2">
                                    {day}
                                </div>
                            ))}
                            {calendarDays.map((day, index) => {
                                if (!day) return <div key={index} />;
                                
                                const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                                const entry = dailyHours.find(e => e.employee === employee && e.date === dateStr);
                                const dayOfWeek = new Date(currentYear, currentMonth, day).getDay();
                                const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                                const isToday = dateStr === today;
                                const isHoliday = holidayMap.has(dateStr);
                                
                                return (
                                    <div
                                        key={index}
                                        className={`
                                            p-2 text-center rounded relative
                                            ${isToday ? 'ring-2 ring-blue-500' : ''}
                                            ${entry ? 'bg-green-100' : isHoliday ? 'bg-orange-100' : isWeekend ? 'bg-gray-50' : 'bg-red-50'}
                                        `}
                                        title={isHoliday ? holidayMap.get(dateStr) : undefined}
                                    >
                                        <div className="text-sm font-medium">{day}</div>
                                        {entry && (
                                            <div className="text-xs text-green-700">{entry.hours}h</div>
                                        )}
                                        {isHoliday && (
                                            <div className="text-xs text-orange-600" title={holidayMap.get(dateStr)}>
                                                🎊
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
                
                {/* Holiday Legend */}
                <div className="mt-4 p-3 bg-gray-50 rounded">
                    <p className="text-sm font-medium text-gray-700 mb-2">Legende:</p>
                    <div className="flex flex-wrap gap-4 text-xs">
                        <span className="flex items-center gap-1">
                            <div className="w-4 h-4 bg-green-100 rounded"></div> Arbeitszeit eingetragen
                        </span>
                        <span className="flex items-center gap-1">
                            <div className="w-4 h-4 bg-red-50 rounded"></div> Keine Arbeitszeit
                        </span>
                        <span className="flex items-center gap-1">
                            <div className="w-4 h-4 bg-orange-100 rounded"></div> Feiertag 🎊
                        </span>
                        <span className="flex items-center gap-1">
                            <div className="w-4 h-4 bg-gray-50 rounded"></div> Wochenende
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}

interface OrderDetailModalProps {
    order: any;
    orders: any[];
    setOrders: React.Dispatch<React.SetStateAction<any[]>>;
    onClose: () => void;
    calculateTotalHours: (timeEntries: any[]) => number;
    updateOrderSafely: (orderId: string, updates: any) => Promise<boolean>;
    currentUser: string;
}

function OrderDetailModal({ order, orders, setOrders, onClose, calculateTotalHours, updateOrderSafely, currentUser }: OrderDetailModalProps) {
    const [editingMaterial, setEditingMaterial] = useState<string | null>(null);
    const [newMaterial, setNewMaterial] = useState({ quantity: '', name: '' });
    const [editTasks, setEditTasks] = useState(false);
    const [editCustomer, setEditCustomer] = useState(false);
    const [editVehicle, setEditVehicle] = useState(false);
    const [tempTasks, setTempTasks] = useState(order.tasks || '');
    const [tempCustomer, setTempCustomer] = useState(order.customerName || '');
    const [tempVehicle, setTempVehicle] = useState(order.vehicle || '');
    const [tasksPriority, setTasksPriority] = useState(order.tasksPriority || 'medium');

    const updateOrder = async (updates: any) => {
        const success = await updateOrderSafely(order.id, updates);
        if (!success) {
            alert('Fehler beim Speichern. Bitte versuchen Sie es erneut.');
        }
    };

    const handleTaskToggle = async (taskId: string, checked: boolean) => {
        const updatedText = updateTasksText(order.tasks, taskId, checked);
        await updateOrder({ tasks: updatedText });
        setTempTasks(updatedText);
    };

    const handlePriorityChange = async (newPriority: string) => {
        setTasksPriority(newPriority);
        await updateOrder({ tasksPriority: newPriority });
    };

    const tasksData = parseTasksWithCheckboxes(order.tasks, tasksPriority);

    const addMaterial = async () => {
        if (!newMaterial.name.trim()) return;
        const materialToAdd = {
            id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            quantity: newMaterial.quantity || '1',
            name: newMaterial.name.trim()
        };
        await updateOrder({ materials: [...(order.materials || []), materialToAdd] });
        setNewMaterial({ quantity: '', name: '' });
    };

    const updateMaterial = async (materialId: string, updates: any) => {
        const updatedMaterials = order.materials.map((m: any) => 
            m.id === materialId ? { ...m, ...updates } : m
        );
        await updateOrder({ materials: updatedMaterials });
        setEditingMaterial(null);
    };

    const deleteMaterial = async (materialId: string) => {
        if (window.confirm('Material löschen?')) {
            const updatedMaterials = order.materials.filter((m: any) => m.id !== materialId);
            await updateOrder({ materials: updatedMaterials });
        }
    };

    const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !order) return;

        // Größenbeschränkung: 15MB
        const maxSize = 15 * 1024 * 1024;
        if (file.size > maxSize) {
            alert('Foto ist zu groß! Maximal 15MB erlaubt.');
            return;
        }

        // Nur Bildformate erlauben
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            alert('Nur Bildformate (JPEG, PNG, GIF, WebP) sind erlaubt!');
            return;
        }

        try {
            // Komprimiere das Bild
            const compressedFile = await compressImage(file);
            const photoURL = await uploadPhotoToFirebase(compressedFile, order.id);
            
            await updateOrder({ photos: [...(order.photos || []), photoURL] });
            alert("Foto wurde hochgeladen!");
        } catch (error) {
            alert('Fehler beim Foto-Upload');
            console.error('Upload-Fehler:', error);
        }
    };

    const deletePhoto = async (index: number) => {
        if (window.confirm('Foto löschen?')) {
            const updatedPhotos = order.photos.filter((_: any, i: number) => i !== index);
            await updateOrder({ photos: updatedPhotos });
        }
    };

    const deleteDocument = async (index: number) => {
        if (window.confirm('Dokument löschen?')) {
            const updatedDocuments = order.documents.filter((_: any, i: number) => i !== index);
            await updateOrder({ documents: updatedDocuments });
        }
    };

    const saveField = async (field: string, value: string) => {
        await updateOrderSafely(order.id, { [field]: value });
        if (field === 'tasks') setEditTasks(false);
        if (field === 'customerName') setEditCustomer(false);
        if (field === 'vehicle') setEditVehicle(false);
    };

    const handleDeleteTimeEntry = async (entryId: string) => {
        const entry = order.timeEntries.find((e: any) => e.id === entryId);
        if (!entry) return;
        
        if (!window.confirm(`Wirklich ${entry.hours}h von ${entry.employee} löschen?`)) return;
        
        const success = await StorageManager.deleteTimeEntry(order.id, entryId, currentUser);
        if (!success) {
            alert('Fehler beim Löschen');
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
            <div className="min-h-screen px-4 text-center">
                <div className="inline-block align-middle bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all my-8 max-w-6xl w-full">
                    <div className="bg-white p-6 border-b">
                        <div className="flex items-center justify-between">
                            <div className="flex-1">
                                {editCustomer ? (
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            value={tempCustomer}
                                            onChange={(e) => setTempCustomer(e.target.value)}
                                            className="text-xl font-semibold border border-gray-300 rounded px-2 py-1"
                                            autoFocus
                                        />
                                        <button 
                                            onClick={() => saveField('customerName', tempCustomer)}
                                            className="text-green-600 hover:text-green-800"
                                        >
                                            ✓
                                        </button>
                                        <button 
                                            onClick={() => {setEditCustomer(false); setTempCustomer(order.customerName);}}
                                            className="text-red-600 hover:text-red-800"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ) : (
                                    <h2 
                                        className="text-xl font-semibold text-gray-800 cursor-pointer hover:bg-gray-50 px-2 py-1 rounded"
                                        onClick={() => setEditCustomer(true)}
                                    >
                                        {order.customerName} ✏️
                                    </h2>
                                )}
                                
                                {editVehicle ? (
                                    <div className="flex items-center gap-2 mt-1">
                                        <input
                                            type="text"
                                            value={tempVehicle}
                                            onChange={(e) => setTempVehicle(e.target.value)}
                                            className="text-gray-600 border border-gray-300 rounded px-2 py-1"
                                            autoFocus
                                        />
                                        <button 
                                            onClick={() => saveField('vehicle', tempVehicle)}
                                            className="text-green-600 hover:text-green-800"
                                        >
                                            ✓
                                        </button>
                                        <button 
                                            onClick={() => {setEditVehicle(false); setTempVehicle(order.vehicle);}}
                                            className="text-red-600 hover:text-red-800"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ) : (
                                    <p 
                                        className="text-gray-600 cursor-pointer hover:bg-gray-50 px-2 py-1 rounded"
                                        onClick={() => setEditVehicle(true)}
                                    >
                                        {order.vehicle} ✏️
                                    </p>
                                )}
                            </div>
                            <button 
                                onClick={onClose}
                                className="text-gray-400 hover:text-gray-600 text-2xl"
                            >
                                ✕
                            </button>
                        </div>
                    </div>

                    <div className="bg-white p-6">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div className="space-y-6">
                                <div>
                                    <h3 className="font-medium text-gray-800 mb-2">Aufgaben</h3>
                                    {editTasks ? (
                                        <div>
                                            <textarea
                                                value={tempTasks}
                                                onChange={(e) => setTempTasks(e.target.value)}
                                                rows={6}
                                                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                autoFocus
                                            />
                                            <div className="flex gap-2 mt-2">
                                                <button 
                                                    onClick={() => saveField('tasks', tempTasks)}
                                                    className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                                                >
                                                    ✓ Speichern
                                                </button>
                                                <button 
                                                    onClick={() => {setEditTasks(false); setTempTasks(order.tasks);}}
                                                    className="bg-gray-300 text-gray-700 px-3 py-1 rounded hover:bg-gray-400"
                                                >
                                                    Abbrechen
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <TaskRenderer 
                                            tasks={tasksData}
                                            originalText={order.tasks}
                                            priority={tasksPriority}
                                            onTaskToggle={handleTaskToggle}
                                            onEditStart={() => setEditTasks(true)}
                                            onPriorityChange={handlePriorityChange}
                                            showEditButton={true}
                                            isChef={true}
                                        />
                                    )}
                                </div>

                                <div>
                                    <h3 className="font-medium text-gray-800 mb-2">Zugewiesen an</h3>
                                    <div className="flex gap-2">
                                        {order.assignedTo?.map((emp: string) => (
                                            <span key={emp} className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-sm">
                                                {emp}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <h3 className="font-medium text-gray-800 mb-2">Arbeitszeiten</h3>
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                        {order.timeEntries?.length > 0 ? (
                                            <div className="space-y-2">
                                                {order.timeEntries.map((entry: any) => (
                                                    <div key={entry.id} className="flex justify-between items-center">
                                                        <div>
                                                            <span className="text-sm font-medium">
                                                                {entry.employee}: {entry.hours}h
                                                            </span>
                                                            <span className="text-xs text-gray-500 ml-2">
                                                                {new Date(entry.timestamp).toLocaleDateString('de-DE')}
                                                            </span>
                                                        </div>
                                                        <button
                                                            onClick={() => handleDeleteTimeEntry(entry.id)}
                                                            className="text-red-500 hover:text-red-700 text-sm"
                                                        >
                                                            🗑️
                                                        </button>
                                                    </div>
                                                ))}
                                                <div className="border-t pt-2 mt-2">
                                                    <strong className="text-sm">
                                                        Gesamt: {calculateTotalHours(order.timeEntries).toFixed(1)}h
                                                    </strong>
                                                </div>
                                            </div>
                                        ) : (
                                            <p className="text-sm text-gray-500">Keine Zeiteinträge</p>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <h3 className="font-medium text-gray-800 mb-2">Status Verlauf</h3>
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                        <div className="space-y-1 text-xs text-gray-600">
                                            {order.statusHistory?.map((entry: any, i: number) => (
                                                <p key={i}>
                                                    <span className="font-bold">{entry.status}</span> am{' '}
                                                    {new Date(entry.timestamp).toLocaleString('de-AT')} durch {entry.by}
                                                </p>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-6">
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="font-medium text-gray-800">Material</h3>
                                    </div>
                                    <div className="space-y-2 mb-4">
                                        {order.materials?.length > 0 ? (
                                            order.materials.map((material: any) => (
                                                <div key={material.id}>
                                                    {editingMaterial === material.id ? (
                                                        <EditMaterialRow 
                                                            material={material}
                                                            onSave={(updates) => updateMaterial(material.id, updates)}
                                                            onCancel={() => setEditingMaterial(null)}
                                                        />
                                                    ) : (
                                                        <div className="flex items-center justify-between bg-gray-50 p-3 rounded">
                                                            <span className="text-sm">
                                                                {material.quantity}x {material.name}
                                                            </span>
                                                            <div className="flex gap-2">
                                                                <button
                                                                    onClick={() => setEditingMaterial(material.id)}
                                                                    className="text-blue-600 hover:text-blue-800 text-sm"
                                                                >
                                                                    ✏️
                                                                </button>
                                                                <button
                                                                    onClick={() => deleteMaterial(material.id)}
                                                                    className="text-red-600 hover:text-red-800 text-sm"
                                                                >
                                                                    🗑️
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-sm text-gray-500 bg-gray-50 p-3 rounded">
                                                Kein Material erfasst
                                            </p>
                                        )}
                                    </div>
                                    
                                    <div className="bg-green-50 p-3 rounded">
                                        <div className="flex gap-2 mb-2">
                                            <input
                                                type="text"
                                                placeholder="Menge"
                                                value={newMaterial.quantity}
                                                onChange={(e) => setNewMaterial(prev => ({ ...prev, quantity: e.target.value }))}
                                                className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                                            />
                                            <input
                                                type="text"
                                                placeholder="Artikelname"
                                                value={newMaterial.name}
                                                onChange={(e) => setNewMaterial(prev => ({ ...prev, name: e.target.value }))}
                                                className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                                            />
                                        </div>
                                        <button 
                                            onClick={addMaterial}
                                            className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                                        >
                                            ✓ Material hinzufügen
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="font-medium text-gray-800">Fotos</h3>
                                        <label className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 cursor-pointer">
                                            📷 Fotos hochladen
                                            <input
                                                type="file"
                                                multiple
                                                accept="image/*"
                                                onChange={handlePhotoUpload}
                                                className="hidden"
                                            />
                                        </label>
                                    </div>
                                    {order.photos?.length > 0 ? (
                                        <div className="grid grid-cols-3 gap-2">
                                            {order.photos.map((url: string, i: number) => (
                                                <div key={i} className="relative group">
                                                    <img 
                                                        src={url} 
                                                        alt={`Foto ${i+1}`} 
                                                        className="w-full h-20 object-cover rounded border cursor-pointer"
                                                        onClick={() => window.open(url, '_blank')}
                                                    />
                                                    <button
                                                        onClick={() => deletePhoto(i)}
                                                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-gray-500 bg-gray-50 p-3 rounded">
                                            Keine Fotos vorhanden
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="font-medium text-gray-800">Dokumente</h3>
                                        <label className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 cursor-pointer">
                                            📄 Dokument hochladen
                                            <input
                                                type="file"
                                                multiple
                                                accept=".pdf,.doc,.docx,.txt,.xlsx,.xls"
                                                onChange={(e) => {
                                                    alert('Dokument-Upload in Demo deaktiviert');
                                                    e.target.value = '';
                                                }}
                                                className="hidden"
                                            />
                                        </label>
                                    </div>
                                    {order.documents?.length > 0 ? (
                                        <div className="space-y-2">
                                            {order.documents.map((doc: any, i: number) => (
                                                <div key={i} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-sm">📄</span>
                                                        <div>
                                                            <p className="text-sm font-medium">{doc.name}</p>
                                                            <p className="text-xs text-gray-500">
                                                                {(doc.size / 1024).toFixed(1)} KB
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <a
                                                            href={doc.url}
                                                            download={doc.name}
                                                            className="text-blue-600 hover:text-blue-800 text-sm"
                                                        >
                                                            ⬇️
                                                        </a>
                                                        <button
                                                            onClick={() => deleteDocument(i)}
                                                            className="text-red-600 hover:text-red-800 text-sm"
                                                        >
                                                            🗑️
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-gray-500 bg-gray-50 p-3 rounded">
                                            Keine Dokumente vorhanden
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <h3 className="font-medium text-gray-800 mb-2">Status ändern</h3>
                                    <select 
                                        value={order.status}
                                        onChange={(e) => updateOrderSafely(order.id, { status: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="Neu">Neu</option>
                                        <option value="In Arbeit">In Arbeit</option>
                                        <option value="Erledigt">Erledigt</option>
                                        <option value="Archiviert">Archiviert</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

interface EditMaterialRowProps {
    material: any;
    onSave: (updates: any) => void;
    onCancel: () => void;
}

function EditMaterialRow({ material, onSave, onCancel }: EditMaterialRowProps) {
    const [quantity, setQuantity] = useState(material.quantity);
    const [name, setName] = useState(material.name);

    const handleSave = () => {
        if (!name.trim()) return;
        onSave({ quantity: quantity || '1', name: name.trim() });
    };

    return (
        <div className="bg-blue-50 p-3 rounded">
            <div className="flex gap-2 mb-2">
                <input
                    type="text"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    placeholder="Menge"
                    className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                />
                <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Artikelname"
                    className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                />
            </div>
            <div className="flex gap-2">
                <button 
                    onClick={handleSave}
                    className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                >
                    ✓ Speichern
                </button>
                <button 
                    onClick={onCancel}
                    className="bg-gray-300 text-gray-700 px-3 py-1 rounded text-sm hover:bg-gray-400"
                >
                    Abbrechen
                </button>
            </div>
        </div>
    );
}

// ERWEITERTE MessageModal mit Prioritäten
interface MessageModalProps {
    onClose: () => void;
    messages: any[];
    setMessages: (messages: any[]) => Promise<void>;
}

function MessageModal({ onClose, messages, setMessages }: MessageModalProps) {
    const [recipients, setRecipients] = useState<string[]>([]);
    const [message, setMessage] = useState('');
    const [priority, setPriority] = useState<'low' | 'medium' | 'high'>('medium');
    
    const handleRecipientChange = (employee: string) => {
        if (recipients.includes(employee)) {
            setRecipients(recipients.filter(e => e !== employee));
        } else {
            setRecipients([...recipients, employee]);
        }
    };
    
    const handleSelectAll = () => {
        if (recipients.length === MITARBEITER.length) {
            setRecipients([]);
        } else {
            setRecipients([...MITARBEITER]);
        }
    };
    
    const handleSend = async () => {
        if (recipients.length === 0) {
            alert('Bitte mindestens einen Empfänger auswählen');
            return;
        }
        if (!message.trim()) {
            alert('Bitte eine Nachricht eingeben');
            return;
        }
        
        const newMessage = {
            id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            from: 'Chef',
            to: recipients,
            message: message.trim(),
            priority: priority,
            createdAt: new Date().toISOString(),
            read: false
        };
        
        // In Firebase speichern
        const updatedMessages = [...messages, newMessage];
        await setMessages(updatedMessages);
        
        const recipientNames = recipients.length === MITARBEITER.length ? 'Alle' : recipients.join(', ');
        const priorityText = priority === 'high' ? '🔴 Hoch' : priority === 'low' ? '🟢 Niedrig' : '🟡 Mittel';
        
        alert(`✅ Nachricht gesendet!\n\nAn: ${recipientNames}\n\nNachricht: ${message}\n\nPriorität: ${priorityText}`);
        
        setMessage('');
        setRecipients([]);
        setPriority('medium');
        onClose();
    };
    
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-md w-full">
                <div className="p-6 border-b">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-semibold">Nachricht an Mitarbeiter</h2>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">
                            ✕
                        </button>
                    </div>
                </div>
                
                <div className="p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Empfänger
                        </label>
                        <div className="space-y-2">
                            <label className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={recipients.length === MITARBEITER.length}
                                    onChange={handleSelectAll}
                                    className="rounded"
                                />
                                <span className="font-medium">Alle auswählen</span>
                            </label>
                            {MITARBEITER.map(employee => (
                                <label key={employee} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={recipients.includes(employee)}
                                        onChange={() => handleRecipientChange(employee)}
                                        className="rounded"
                                    />
                                    <span>{employee}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Priorität
                        </label>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setPriority('low')}
                                className={`flex-1 px-3 py-2 rounded-md border ${
                                    priority === 'low'
                                        ? 'bg-green-50 border-green-500 text-green-700'
                                        : 'bg-white border-gray-300 text-gray-700'
                                }`}
                            >
                                🟢 Niedrig
                            </button>
                            <button
                                onClick={() => setPriority('medium')}
                                className={`flex-1 px-3 py-2 rounded-md border ${
                                    priority === 'medium'
                                        ? 'bg-yellow-50 border-yellow-500 text-yellow-700'
                                        : 'bg-white border-gray-300 text-gray-700'
                                }`}
                            >
                                🟡 Mittel
                            </button>
                            <button
                                onClick={() => setPriority('high')}
                                className={`flex-1 px-3 py-2 rounded-md border ${
                                    priority === 'high'
                                        ? 'bg-red-50 border-red-500 text-red-700'
                                        : 'bg-white border-gray-300 text-gray-700'
                                }`}
                            >
                                🔴 Hoch
                            </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                            {priority === 'high' && 'Sofortige Push-Benachrichtigung'}
                            {priority === 'medium' && 'Normale Benachrichtigung'}
                            {priority === 'low' && 'Stille Benachrichtigung'}
                        </p>
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Nachricht
                        </label>
                        <textarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            rows={4}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            placeholder="Deine Nachricht hier..."
                            autoFocus
                        />
                        <div className="mt-2 flex flex-wrap gap-2">
                            <span className="text-xs text-gray-500">Schnell-Vorlagen:</span>
                            <button
                                onClick={() => setMessage('Bitte heute Abend die Werkstatt aufräumen!')}
                                className="text-xs bg-gray-100 px-2 py-1 rounded hover:bg-gray-200"
                            >
                                Aufräumen
                            </button>
                            <button
                                onClick={() => setMessage('Meeting morgen um 8:00 Uhr - bitte pünktlich sein!')}
                                className="text-xs bg-gray-100 px-2 py-1 rounded hover:bg-gray-200"
                            >
                                Meeting
                            </button>
                            <button
                                onClick={() => setMessage('Feierabend für heute! Schönen Abend 👍')}
                                className="text-xs bg-gray-100 px-2 py-1 rounded hover:bg-gray-200"
                            >
                                Feierabend
                            </button>
                            <button
                                onClick={() => setMessage('WICHTIG: Kunde kommt gleich - bitte Werkstatt vorbereiten!')}
                                className="text-xs bg-gray-100 px-2 py-1 rounded hover:bg-gray-200"
                            >
                                Kunde kommt
                            </button>
                        </div>
                    </div>
                    
                    <div className="text-xs text-gray-500 bg-blue-50 p-3 rounded">
                        <p className="font-medium mb-1">💡 Prioritäten-Info:</p>
                        <ul className="space-y-1 ml-4">
                            <li>• <span className="text-green-600">Niedrig:</span> Wird im Hintergrund angezeigt</li>
                            <li>• <span className="text-yellow-600">Mittel:</span> Normale Benachrichtigung</li>
                            <li>• <span className="text-red-600">Hoch:</span> Sofort sichtbar mit Ton/Vibration</li>
                        </ul>
                    </div>
                </div>
                
                <div className="p-6 border-t flex gap-3">
                    <button
                        onClick={onClose}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                        Abbrechen
                    </button>
                    <button
                        onClick={handleSend}
                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                        📨 Senden
                    </button>
                </div>
            </div>
        </div>
    );
}

// NEUE KOMPONENTE: Arbeitszeiten-Modal für Chef
interface ChefDailyHoursModalProps {
    onClose: () => void;
    dailyHours: any[];
    onSave: (hours: number, date: string, employee: string) => Promise<void>;
}

function ChefDailyHoursModal({ onClose, dailyHours, onSave }: ChefDailyHoursModalProps) {
    const [selectedEmployee, setSelectedEmployee] = useState<string>(MITARBEITER[0]);
    const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
    const [hours, setHours] = useState<string>('');
    const [viewMode, setViewMode] = useState<'input' | 'overview'>('input');
    
    // Get existing entry
    const existingEntry = dailyHours.find(entry => 
        entry.employee === selectedEmployee && entry.date === selectedDate
    );
    
    useEffect(() => {
        if (existingEntry) {
            setHours(existingEntry.hours.toString());
        } else {
            setHours('');
        }
    }, [selectedEmployee, selectedDate, existingEntry]);
    
    const handleSave = async () => {
        const hoursFloat = parseFloat(hours);
        if (!hoursFloat || hoursFloat <= 0 || hoursFloat > 24) {
            alert('Bitte gültige Stundenzahl eingeben (0-24)');
            return;
        }
        
        await onSave(hoursFloat, selectedDate, selectedEmployee);
        alert(`✅ ${hoursFloat}h für ${selectedEmployee} am ${new Date(selectedDate).toLocaleDateString('de-DE')} gespeichert`);
        setHours('');
    };
    
    // Statistiken berechnen
    const last30Days = new Date();
    last30Days.setDate(last30Days.getDate() - 30);
    
    const employeeStats = MITARBEITER.map(emp => {
        const entries = dailyHours.filter(e => 
            e.employee === emp && new Date(e.date) >= last30Days
        );
        const total = entries.reduce((sum, e) => sum + e.hours, 0);
        return {
            name: emp,
            total,
            average: entries.length > 0 ? total / entries.length : 0,
            daysWorked: entries.length
        };
    });
    
    const handleDateClick = (date: string): void => {
        setSelectedDate(date);
        setViewMode('input');
    };
    
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden">
                <div className="p-6 border-b">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-semibold">Arbeitszeiten verwalten</h2>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">
                            ✕
                        </button>
                    </div>
                    
                    <div className="flex gap-2 mt-4">
                        <button
                            onClick={() => setViewMode('input')}
                            className={`px-4 py-2 rounded ${
                                viewMode === 'input' 
                                    ? 'bg-blue-600 text-white' 
                                    : 'bg-gray-200 text-gray-700'
                            }`}
                        >
                            Eingabe
                        </button>
                        <button
                            onClick={() => setViewMode('overview')}
                            className={`px-4 py-2 rounded ${
                                viewMode === 'overview' 
                                    ? 'bg-blue-600 text-white' 
                                    : 'bg-gray-200 text-gray-700'
                            }`}
                        >
                            Übersicht
                        </button>
                    </div>
                </div>
                
                <div className="p-6">
                    {viewMode === 'input' ? (
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Mitarbeiter
                                </label>
                                <select
                                    value={selectedEmployee}
                                    onChange={(e) => setSelectedEmployee(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                >
                                    {MITARBEITER.map(emp => (
                                        <option key={emp} value={emp}>{emp}</option>
                                    ))}
                                </select>
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Datum
                                </label>
                                <input
                                    type="date"
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                    max={new Date().toISOString().split('T')[0]}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Arbeitsstunden
                                </label>
                                <input
                                    type="number"
                                    step="0.5"
                                    value={hours}
                                    onChange={(e) => setHours(e.target.value)}
                                    placeholder="z.B. 8.5"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-lg"
                                />
                                {existingEntry && (
                                    <p className="text-sm text-yellow-600 mt-1">
                                        ⚠️ Überschreibt existierenden Eintrag ({existingEntry.hours}h)
                                    </p>
                                )}
                            </div>
                            
                            <button
                                onClick={handleSave}
                                className="w-full bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700"
                            >
                                {existingEntry ? 'Aktualisieren' : 'Speichern'}
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <h3 className="font-medium">Letzte 30 Tage Übersicht</h3>
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b">
                                            <th className="text-left py-2">Mitarbeiter</th>
                                            <th className="text-center py-2">Gesamt</th>
                                            <th className="text-center py-2">Ø pro Tag</th>
                                            <th className="text-center py-2">Arbeitstage</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {employeeStats.map(stat => (
                                            <tr key={stat.name} className="border-b">
                                                <td className="py-2">{stat.name}</td>
                                                <td className="text-center font-medium">
                                                    {stat.total.toFixed(1)}h
                                                </td>
                                                <td className="text-center">
                                                    {stat.average.toFixed(1)}h
                                                </td>
                                                <td className="text-center">
                                                    {stat.daysWorked}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            
                            {/* Monatskalender */}
                            <div className="mt-6">
                                <h4 className="font-medium mb-2">Aktueller Monat</h4>
                                <MonthCalendar 
                                    dailyHours={dailyHours}
                                    employee={selectedEmployee}
                                    onDateClick={handleDateClick}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// Hilfskomponente für Monatskalender
interface MonthCalendarProps {
    dailyHours: any[];
    employee: string;
    onDateClick: (date: string) => void;
}

function MonthCalendar({ dailyHours, employee, onDateClick }: MonthCalendarProps) {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const today = now.toISOString().split('T')[0];
    
    // Get holidays
    const holidays = getAustrianHolidays(currentYear);
    const holidayMap = new Map(holidays.map(h => [h.date, h.name]));
    
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const startingDayOfWeek = firstDay.getDay();
    
    const calendarDays: (number | null)[] = [];
    const adjustedStart = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;
    
    for (let i = 0; i < adjustedStart; i++) {
        calendarDays.push(null);
    }
    
    for (let day = 1; day <= lastDay.getDate(); day++) {
        calendarDays.push(day);
    }
    
    return (
        <div className="grid grid-cols-7 gap-1">
            {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(day => (
                <div key={day} className="text-center text-xs font-medium text-gray-500 p-2">
                    {day}
                </div>
            ))}
            {calendarDays.map((day, index) => {
                if (!day) return <div key={index} />;
                
                const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const entry = dailyHours.find((e: any) => e.employee === employee && e.date === dateStr);
                const dayOfWeek = new Date(currentYear, currentMonth, day).getDay();
                const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                const isToday = dateStr === today;
                const isHoliday = holidayMap.has(dateStr);
                
                return (
                    <div
                        key={index}
                        onClick={() => onDateClick(dateStr)}
                        className={`
                            p-2 text-center rounded cursor-pointer hover:bg-blue-100
                            ${isToday ? 'ring-2 ring-blue-500' : ''}
                            ${entry ? 'bg-green-100' : isHoliday ? 'bg-orange-100' : isWeekend ? 'bg-gray-50' : 'bg-red-50'}
                        `}
                        title={isHoliday ? holidayMap.get(dateStr) : undefined}
                    >
                        <div className="text-sm font-medium">{day}</div>
                        {entry && (
                            <div className="text-xs text-green-700">{entry.hours}h</div>
                        )}
                        {isHoliday && (
                            <div className="text-xs text-orange-600">🎊</div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

interface DeletedOrdersViewProps {
    orders: any[];
    onRestore: (orderId: string) => void;
    onPermanentDelete: (orderId: string) => void;
}

function DeletedOrdersView({ orders, onRestore, onPermanentDelete }: DeletedOrdersViewProps) {
    if (orders.length === 0) {
        return (
            <div className="max-w-7xl mx-auto p-8 text-center">
                <p className="text-gray-500">Keine gelöschten Aufträge vorhanden</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <h2 className="text-xl font-semibold mb-4 text-red-700">
                Gelöschte Aufträge ({orders.length})
            </h2>
            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mb-4">
                <p className="text-sm text-yellow-800">
                    ⚠️ Gelöschte Aufträge werden nach 30 Tagen automatisch entfernt.
                </p>
            </div>
            <div className="grid gap-4">
                {orders.map(order => (
                    <div key={order.id} className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex justify-between items-start">
                            <div className="flex-1">
                                <h3 className="font-semibold">{order.customerName}</h3>
                                <p className="text-sm text-gray-600">{order.vehicle}</p>
                                <p className="text-xs text-red-600 mt-2">
                                    Gelöscht am {new Date(order.deletedAt).toLocaleString('de-DE')} 
                                    von {order.deletedBy}
                                </p>
                                <p className="text-xs text-gray-500">
                                    Vorheriger Status: {order.previousStatus}
                                </p>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => onRestore(order.id)}
                                    className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
                                >
                                    ♻️ Wiederherstellen
                                </button>
                                <button
                                    onClick={() => onPermanentDelete(order.id)}
                                    className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                                >
                                    ⚠️ Endgültig löschen
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

interface RecoveryModalProps {
    onClose: () => void;
    onRestore: (orderId: string, backupId: string) => void;
    orders: any[];
}

function RecoveryModal({ onClose, onRestore, orders }: RecoveryModalProps) {
    const [selectedOrder, setSelectedOrder] = useState<string>('');
    const [backups, setBackups] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    
    useEffect(() => {
        if (selectedOrder) {
            loadBackupsForOrder(selectedOrder);
        }
    }, [selectedOrder]);
    
    const loadBackupsForOrder = async (orderId: string) => {
        setLoading(true);
        try {
            const backupsRef = ref(database, `${StorageManager.KEYS.BACKUPS}/${orderId}`);
            const snapshot = await get(backupsRef);
            
            if (snapshot.exists()) {
                const backupsData = snapshot.val();
                const backupsList = Object.entries(backupsData).map(([key, value]: any) => ({
                    id: key,
                    ...value
                }));
                setBackups(backupsList.sort((a, b) => 
                    new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
                ));
            } else {
                setBackups([]);
            }
        } catch (error) {
            console.error('Fehler beim Laden der Backups:', error);
            setBackups([]);
        }
        setLoading(false);
    };
    
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden">
                <div className="p-6 border-b">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-semibold">Backup & Wiederherstellung</h2>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">
                            ✕
                        </button>
                    </div>
                </div>
                
                <div className="p-6">
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Auftrag auswählen
                        </label>
                        <select
                            value={selectedOrder}
                            onChange={(e) => setSelectedOrder(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        >
                            <option value="">-- Auftrag wählen --</option>
                            {orders.map(order => (
                                <option key={order.id} value={order.id}>
                                    {order.customerName} - {order.vehicle}
                                </option>
                            ))}
                        </select>
                    </div>
                    
                    {selectedOrder && (
                        <div>
                            <h3 className="font-medium mb-2">Verfügbare Backups:</h3>
                            <div className="space-y-2 max-h-60 overflow-y-auto">
                                {loading ? (
                                    <p className="text-gray-500 text-center py-4">Lade Backups...</p>
                                ) : backups.length > 0 ? (
                                    backups.map(backup => (
                                        <div key={backup.id} className="flex items-center justify-between p-3 bg-gray-50 rounded border border-gray-200 hover:bg-gray-100">
                                            <div className="flex-1">
                                                <p className="font-medium text-sm">
                                                    {backup.action}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    {new Date(backup.timestamp).toLocaleString('de-DE')}
                                                    {' • '}
                                                    von {backup.user}
                                                </p>
                                            </div>
                                            <button
                                                onClick={() => {
                                                    if (window.confirm('Wirklich dieses Backup wiederherstellen?')) {
                                                        onRestore(selectedOrder, backup.id);
                                                    }
                                                }}
                                                className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                                            >
                                                Wiederherstellen
                                            </button>
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-gray-500 text-center py-4">Keine Backups für diesen Auftrag</p>
                                )}
                            </div>
                        </div>
                    )}
                    
                    <div className="mt-6 bg-blue-50 p-3 rounded">
                        <p className="text-sm text-blue-800">
                            <strong>💡 Info:</strong> Es werden automatisch die letzten 50 Backups pro Auftrag gespeichert. 
                            Jede Änderung wird vor der Durchführung gesichert.
                        </p>
                    </div>
                </div>
                
                <div className="p-6 border-t">
                    <button
                        onClick={onClose}
                        className="w-full py-2 bg-gray-300 rounded hover:bg-gray-400"
                    >
                        Schließen
                    </button>
                </div>
            </div>
        </div>
    );
}